import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Spin,
  Row,
  Col,
  Tag,
  Typography,
  Divider,
  message,
  Empty,
  Space,
  Statistic,
  Table,
  Avatar,
  Progress,
  Tabs,
  Input,
  List,
  Button,
  Popconfirm,
  Modal,
  Image,
  Collapse,
} from "antd";
import {
  UserOutlined,
  CarOutlined,
  CalendarOutlined,
  BankOutlined,
  HomeOutlined,
  TeamOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  WarningOutlined,
  PhoneOutlined,
  IdcardOutlined,
  SafetyCertificateOutlined,
  SolutionOutlined,
  AuditOutlined,
  TwitchOutlined,
  DownSquareOutlined,
  UpSquareOutlined,
  SaveOutlined,
  EditOutlined,
  CloseOutlined,
  FileSearchOutlined,
  EyeOutlined,
  FilePdfOutlined,
  TransactionOutlined,
  FolderOpenOutlined,
  FileProtectOutlined,
  MailOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import axios from "axios";
import MotionHoc from "../../../utils/MotionHoc";
import {
  BASE_URL_BEN,
  GET_DATA_EMS,
  GET_LOAD_IMAGE_NEW_ALL,
  GET_LOAD_IMAGE_NEW_BY,
  GET_STATUS_THAIPOST,
  HEADERS_EXPORT,
  HEADERS_EXPORT_BEN,
  HEADERS_EXPORT_DETAIL,
  HEADERS_UPIMAGE,
  POST_APPROVED_DUE,
  POST_BILL_PAYMENT,
  POST_CONTNO_DETAIL_SEARCH,
  POST_FOLLOW_CUSTOMER,
  POST_INSERT_NOTE_NEW,
  POST_JUDGE_BEEYPUP,
  POST_LOAD_DATA_DUEPAYMENT,
  POST_LOAD_DATA_DUEPAYMENT_JUDGE,
  POST_LOAD_MEMO_NEW,
  POST_LOAN_DB2,
  POST_PREVIEW_BEEYPUP,
  POST_UPDATE_MEMO,
} from "../../API/apiUrls";
import { useSearch } from "./hook/SearchContext";
import dayjs from "dayjs";
import PayInSlipModal from "../afterCourt/report/QRcode";
import TokenCheck from "../../../hook/TokenCheck";
import AddImage from "./modal/AddImage";
import PreviewPayment from "./modal/PreviewPayment";
import BillAfterCourtOldData from "../afterCourt/modal/BillAfterCourtOldData";

import { PaymentLawyerPdfJs } from "./export/PaymentLawyerPdfJs";
import { PaymentArLawyerPdfjs } from "./export/PaymentArLawyerPdfjs";
import { exportDuePaymentPdf } from "./export/DuePayment";
import { exportCardDebtorPdf } from "./export/CardDebtor";

const { Title, Text } = Typography;

// ==================== THEME CONFIG ====================
const THEME = {
  primary: "#1a365d",
  secondary: "#2d4a6f",
  accent: "#e67e22",
  success: "#27ae60",
  warning: "#f39c12",
  danger: "#e74c3c",
  info: "#3498db",
  light: "#f8fafc",
  dark: "#1e293b",
  gradient: "linear-gradient(135deg, #1a365d 0%, #2d4a6f 100%)",
  cardShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  borderRadius: "12px",
};

// ==================== UTILITIES ====================
const formatMoney = (value) => {
  if (!value && value !== 0) return "0";
  return Number(value).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("th-TH", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// รวมตารางดิว (ทำยอม) กับข้อมูลเบี้ยปรับ — ถ้างวดไหนมีเบี้ยปรับ ให้แทรกต่อจากแถวดิวงวดนั้น
const mergeDuePaymentWithPenalty = (dueRows = [], penaltyRows = []) => {
  const sortedDue = [...(dueRows || [])].sort(
    (a, b) => Number(a.NOPAY) - Number(b.NOPAY),
  );
  const merged = [];
  sortedDue.forEach((due) => {
    merged.push({ ...due, __rowType: "due", _key: `due-${due.id}` });
    (penaltyRows || [])
      .filter((p) => Number(p.NOPAY) === Number(due.NOPAY))
      .forEach((p, idx) => {
        merged.push({
          ...p,
          __rowType: "penalty",
          _key: `penalty-${due.NOPAY}-${idx}`,
        });
      });
  });
  return merged;
};

// ==================== STYLED COMPONENTS ====================
const StyledCard = ({ children, title, icon, extra, style, ...props }) => (
  <Card
    title={
      title && (
        <Space>
          {icon}
          <Text strong style={{ fontSize: 16, color: THEME.primary }}>
            {title}
          </Text>
        </Space>
      )
    }
    extra={extra}
    style={{
      boxShadow: THEME.cardShadow,
      border: "none",
      ...style,
      background: THEME.light,
      borderBottom: `2px solid ${THEME.primary}`,
      borderRadius: `${THEME.borderRadius} ${THEME.borderRadius} 0 0`,
    }}
    {...props}
  >
    {children}
  </Card>
);

const StatBox = ({
  title,
  value,
  suffix = "บาท",
  color = THEME.primary,
  icon,
}) => (
  <div
    style={{
      background: `${color}08`,
      padding: "16px 20px",
      borderRadius: "10px",
      borderLeft: `4px solid ${color}`,
    }}
  >
    <Space align="start">
      {icon && (
        <div
          style={{
            background: color,
            padding: 8,
            borderRadius: 8,
            color: "#fff",
          }}
        >
          {icon}
        </div>
      )}
      <div>
        <Text type="secondary" style={{ fontSize: 16, display: "block" }}>
          {title}
        </Text>
        <Text strong style={{ fontSize: 18, color }}>
          {formatMoney(value)}
        </Text>
        <Text type="secondary" style={{ fontSize: 16, marginLeft: 4 }}>
          {suffix}
        </Text>
      </div>
    </Space>
  </div>
);

const InfoItem = ({ label, value, icon }) => (
  <div style={{ marginBottom: 12 }}>
    <Text type="secondary" style={{ fontSize: 16, display: "block" }}>
      {icon && <span style={{ marginRight: 6 }}>{icon}</span>}
      {label}
    </Text>
    <Text strong style={{ fontSize: 18 }}>
      {value || "-"}
    </Text>
  </div>
);

// ==================== LEGACY (Delphi-style) FORM COMPONENTS ====================
const LegacySectionBar = ({ children }) => (
  <div
    style={{
      background: "#dce6f1",
      border: "1px solid #b8c4d0",
      borderRadius: 4,
      padding: "4px 10px",
      fontWeight: 700,
      color: THEME.primary,
      fontSize: 14,
      marginBottom: 8,
    }}
  >
    {children}
  </div>
);

const LegacyField = ({ label, value, span = 4, valueColor = "#0b3d91" }) => (
  <Col xs={12} sm={8} md={span}>
    <div
      style={{
        border: "1px solid #c3d0e0",
        borderRadius: 4,
        padding: "4px 8px",
        background: "#fff",
        height: "100%",
      }}
    >
      <div style={{ fontSize: 12, color: "#5b6b7c", lineHeight: 1.3 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: valueColor,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={typeof value === "string" ? value : undefined}
      >
        {value === null || value === undefined || value === "" ? "-" : value}
      </div>
    </div>
  </Col>
);

const LegacyPersonBlock = ({
  title,
  relatn,
  cuscod,
  birthdt,
  addr,
  occup,
  offic,
}) => (
  <div style={{ fontSize: 14, lineHeight: 1.9 }}>
    <div>
      <Text strong>ชื่อ: </Text>
      {title || "-"}
    </div>
    {relatn && (
      <div>
        <Text strong>ความสัมพันธ์: </Text>
        {relatn}
      </div>
    )}
    <div>
      <Text strong>เลขที่บัตร: </Text>
      {cuscod || "-"}
    </div>
    <div>
      <Text strong>วันเกิด: </Text>
      {formatDate(birthdt)}
    </div>
    <div>
      <Text strong>ที่อยู่: </Text>
      {addr ? `${addr.ADDR1 || ""} ${addr.ADDR2 || ""}`.trim() || "-" : "-"}
    </div>
    <div>
      <Text strong>ตำบล: </Text>
      {addr?.TUMB || "-"}
      <Text strong style={{ marginLeft: 12 }}>
        อำเภอ:{" "}
      </Text>
      {addr?.AUMPDES || "-"}
    </div>
    <div>
      <Text strong>จังหวัด: </Text>
      {addr?.PROVDES || "-"}
      <Text strong style={{ marginLeft: 12 }}>
        รหัสไปรษณีย์:{" "}
      </Text>
      {addr?.ZIP || "-"}
    </div>
    <div>
      <Text strong>เบอร์โทร: </Text>
      {addr?.TELP || "-"}
    </div>
    <div>
      <Text strong>ที่ทำงาน: </Text>
      {offic || "-"} {occup ? `(${occup})` : ""}
    </div>
  </div>
);

const PdfPreviewModal = ({ open, url, onClose }) => (
  <Modal
    open={open}
    onCancel={onClose}
    footer={null}
    width="90vw"
    style={{ top: 10 }}
    title="ข้อมูล PDF"
  >
    <iframe
      src={url}
      style={{ width: "100%", height: "80vh", border: "none" }}
      title="pdf-preview"
    />
  </Modal>
);

const getDocLabel = (menuType, imageType) => {
  if (menuType === 5 && imageType === 3)
    return { label: "ไฟล์คำฟ้อง", color: "#d46b08" }; // ส้มเข้ม
  if (menuType === 5 && imageType === 4)
    return { label: "ใบเสร็จศาลคำฟ้อง", color: "#d46b08" }; // ส้มเข้ม
  if (menuType === 6 && imageType === 7)
    return { label: "คำพิพากษา", color: "#096dd9" }; // น้ำเงินเข้ม
  if (menuType === 6 && imageType === 8)
    return { label: "คำพิพากษา(ทำยอม)", color: "#096dd9" }; // น้ำเงินเข้ม
  if (menuType === 6 && imageType === 9)
    return { label: "ถอนฟ้อง", color: "#096dd9" }; // น้ำเงินเข้ม
  if (menuType === 6 && imageType === 10)
    return { label: "ใบเสร็จศาลหลังพิพากษา", color: "#389e0d" }; // เขียวเข้ม
  if (menuType === 6 && imageType === 12)
    return { label: "วางคำบังคับ", color: "#096dd9" }; // น้ำเงินเข้ม
  if (menuType === 8 && imageType === 13)
    return { label: "บัดตัดจ่าย", color: "#096dd9" }; // น้ำเงินเข้ม
  if (menuType === 8 && imageType === 14)
    return { label: "ใบเสร็จปิดบัญชี", color: "#096dd9" }; // น้ำเงินเข้ม
  return { label: "เอกสารอื่น ๆ", color: "#595959" }; // เทาเข้ม
};
// ── ตัดบันทัดว่างซ้ำให้เหลือแค่ 1 บันทัด ──────────────
const collapseBlankLines = (text) =>
  text ? text.replace(/\r\n/g, "\n").replace(/\n{3,}/g, "\n\n") : text;

// ── กำหนด filter ตาม currentStatus / closeContno ──────────────
const getAllowedTypes = (judgeData) => {
  const status = judgeData?.judge?.[0]?.currentStatus;
  const closeContno = judgeData?.judge?.[0]?.closeContno;
  const allowed = [];

  if (status > 3 && status !== 6 && status !== 7) {
    allowed.push({ menuType: 5, imageType: 3 });
    allowed.push({ menuType: 5, imageType: 4 });
  }
  if (status > 7) {
    allowed.push({ menuType: 6, imageType: 7 });
    allowed.push({ menuType: 6, imageType: 8 });
    allowed.push({ menuType: 6, imageType: 9 });
  }
  if (status > 11) {
    allowed.push({ menuType: 6, imageType: 10 });
    allowed.push({ menuType: 6, imageType: 12 });
  }
  if (closeContno === 0) {
    allowed.push({ menuType: 8, imageType: 13 });
    allowed.push({ menuType: 8, imageType: 14 });
  }
  return allowed;
};

const getMenuTypesToLoad = (judgeData) => {
  const allowed = getAllowedTypes(judgeData);
  return [...new Set(allowed.map((a) => a.menuType))];
};

const getFileType = (path = "") => {
  const ext = path.toLowerCase().split(".").pop();
  if (ext === "pdf") return "pdf";
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "image";
  return "other";
};

const mailStatusOptions = [
  {
    value: 1,
    label: "ตอบกลับ",
    icon: "✅ ",
    color: "success", // สีเขียว
  },
  {
    value: 2,
    label: "ตีกลับ",
    icon: "↩️ ",
    color: "error", // สีแดง
  },
  {
    value: 3,
    label: "ส่งถึงแล้วไม่ตอบกลับ",
    icon: "📬 ",
    color: "warning", // สีส้ม/เหลือง
  },
  {
    value: 4,
    label: "ไม่มีการเคลื่อนไหว (เกิน 20 วัน)",
    icon: "🛑 ",
    color: "default", // สีเทา
  },
  {
    value: 5,
    label: "ตอบกลับ(เว็บ)",
    icon: "🌐 ",
    color: "cyan", // ตอบกลับจากเว็บไปรษณีย์
  },
  {
    value: 6,
    label: "ตีกลับ(เว็บ)",
    icon: "🌐 ",
    color: "volcano", // ตีกลับจากเว็บไปรษณีย์
  },
];

// ==================== MAIN COMPONENT ====================
const Main = () => {
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const roleId = parseInt(localStorage.getItem("ROLE_ID"));
  const token = localStorage.getItem("TOKEN");
  const [signOut] = TokenCheck();
  const { TextArea } = Input;

  const [loadingJudge, setLoadingJudge] = useState(false); // loading สำหรับ judgeData
  const [loadingLoan, setLoadingLoan] = useState(false); // loading สำหรับ loanData
  const [loadingImge, setLoadingImge] = useState(false); // loading สำหรับ loanData
  const [loanData, setLoanData] = useState(null);
  const [judgeData, setJudgeData] = useState(null);
  const [activeTab, setActiveTab] = useState("1");
  const [memoDataOld, setMemoDataOld] = useState(null);
  const [memoDataNew, setMemoDataNew] = useState([]);
  const [showNoteOld, setShowNoteOld] = useState(true);
  const [showNoteNew, setShowNoteNew] = useState(true);
  const [editMemoOld, setEditMemoOld] = useState(true);
  const [remarkValueOld, setRemarkValueOld] = useState(null);
  const [remarkValueNew, setRemarkValueNew] = useState(null);
  const [billPayment, setBillPayment] = useState(null);
  const [duePaymentData, setDuePaymentData] = useState([]);
  const [beeypupData, setBeeypupData] = useState([]);
  const [followCusData, setFollowCusData] = useState([]);
  const [expenseListData, setExpenseListData] = useState([]);
  const { searchData, clearSearch, handleSearch } = useSearch();
  const [searchInput, setSearchInput] = useState("");
  const [isModalpayIn, setIsModalPayIn] = useState(false);

  const [fileListByContractCourt, setFileListByContractCourt] = useState([]);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState("");
  const [isModalAddImge, setIsModalAddImge] = useState(false);
  const [isModalPreviewPayment, setIsModalPreviewPayment] = useState(false);
  const [isModalBillAfterCourtOldData, setIsModalBillAfterCourtOldData] =
    useState(false);
  const [statusEms, setStatusEms] = useState([]);
  const [mailData, setMailData] = useState(null); // ✅ เพิ่ม state
  const [loadBeeypup, setLoadBeeypup] = useState(false);
  const [loadApprovDue, setLoadApprovDue] = useState(false);

  useEffect(() => {
    console.log("searchData", searchData);

    setFileListByContractCourt([]);
    setLoanData(null);
    setJudgeData(null);
    setMemoDataOld(null);
    setExpenseListData([]);
    setMemoDataNew([]);
    setDuePaymentData([]);
    setBeeypupData([]);
    setFollowCusData([]);
    setActiveTab("1");
    if (searchData?.CONTNO) {
      // setLoanData(null);
      loadJudgeData(searchData);
      // loadLoanData(searchData?.CONTNO);
      // loadMemoDataOld(searchData?.CONTNO);
    }
  }, [searchData]);

  useEffect(() => {
    console.log(judgeData);

    setFileListByContractCourt([]);
    setLoanData(null);
    setJudgeData(null);
    setMemoDataOld(null);
    setExpenseListData([]);
    setMemoDataNew([]);
    setDuePaymentData([]);
    setBeeypupData([]);
    setFollowCusData([]);
    setActiveTab("1");
  }, []);

  const loadLoanData = async (contno) => {
    const useContno = /^V\d/.test(contno) ? contno.substring(1) : contno;
    setLoadingLoan(true);
    try {
      const res = await axios.post(
        POST_LOAN_DB2,
        { CONTNO: `'${useContno}'` },
        { headers: HEADERS_EXPORT_BEN },
      );
      const data = res?.data?.[0] || null;
      if (data) setLoanData(data);
      return data;
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error loadLoanData:", error);
        message.error("เกิดข้อผิดพลาดในการโหลดข้อมูลสัญญา");
      }
      return null;
    } finally {
      setLoadingLoan(false);
    }
  };

  // constants — ดึงออกไปไว้ไฟล์รวม constants ของ feature นี้
  const USER_IDS = { HIA: 35 };
  const ROLE_IDS = { JUDGE_REVIEWER: 12 };
  const HIA_APPROVED_STATUSES = [18, 22, 23, 24, 25, 26];
  const JUDGMENT_REVIEWED_THRESHOLD = 16;

  const MESSAGES = {
    NO_DATA: "ไม่มีข้อมูล",
    HIA_NOT_APPROVED: "เฮียยังไม่อนุมัติ",
    JUDGMENT_NOT_REVIEWED: "ยังไม่ตรวจคำพิพากษา",
    LOAD_ERROR: "เกิดข้อผิดพลาดในการโหลดข้อมูลคดี",
  };

  // helpers — pure functions, test ง่าย
  const getJudgeValidationMessage = ({
    data,
    userId,
    roleId,
    currentStatus,
  }) => {
    if (data?.details === "ไม่พบข้อมูล") return MESSAGES.NO_DATA;

    if (
      userId === USER_IDS.HIA &&
      !HIA_APPROVED_STATUSES.includes(data?.judge?.[0]?.currentStatus)
    ) {
      return MESSAGES.HIA_NOT_APPROVED;
    }

    const judgeStatus = data?.judge?.[0]?.currentStatus;
    if (
      userId !== USER_IDS.HIA &&
      roleId === ROLE_IDS.JUDGE_REVIEWER &&
      judgeStatus !== undefined &&
      judgeStatus <= JUDGMENT_REVIEWED_THRESHOLD
    ) {
      return MESSAGES.JUDGMENT_NOT_REVIEWED;
    }

    return null;
  };

  const loadJudgeData = async (payload) => {
    setLoadingJudge(true);
    console.log("payload", payload);

    try {
      const { data } = await axios.post(POST_CONTNO_DETAIL_SEARCH, payload, {
        headers: HEADERS_EXPORT_DETAIL,
      });

      if (!data) return;

      // 1) แสดง validation message (ถ้ามี)
      const validationMsg = getJudgeValidationMessage({
        data,
        userId,
        roleId,
      });
      if (validationMsg) message.info(validationMsg);

      // 2) อัปเดต state หลัก
      setJudgeData(data);
      setExpenseListData(data.work_expense_list ?? []);
      console.log("data---->", data);

      console.log("searchData.CONTNO", searchData.CONTNO);
      // 3) จัดการ memo / loan data
      if (data.note) {
        setMemoDataNew(data.note);
      } else {
        setMemoDataNew([]);
        // loadLoanData(searchData.CONTNO);
      }
    } catch (error) {
      handleLoadJudgeError(error);
    } finally {
      setLoadingJudge(false);
    }
  };

  const handleLoadJudgeError = (error) => {
    if (error?.name === "CanceledError") return;

    if (error?.response?.status === 401) {
      signOut();
      return;
    }

    console.error("Error loadJudgeData:", error);
    message.error(MESSAGES.LOAD_ERROR);
  };

  const loadMemoDataOld = async (contno) => {
    const useContno = /^V\d/.test(contno) ? contno.substring(1) : contno;
    setLoadingLoan(true);
    try {
      const res = await axios.post(POST_LOAD_MEMO_NEW, {
        contractNo: useContno,
      });
      if (res?.data) {
        const memo1 = collapseBlankLines(res.data.MEMO1);
        setMemoDataOld({ ...res.data, MEMO1: memo1 });
        setRemarkValueOld(memo1);
      }
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error loadMemoDataOld:", error);
        message.error("เกิดข้อผิดพลาดในการโหลดข้อมูลสัญญา");
      }
    } finally {
      setLoadingLoan(false);
    }
  };

  const loadBillPayment = async () => {
    setLoadingLoan(true);
    try {
      const res = await axios.post(
        POST_BILL_PAYMENT,
        {
          CONTNO: searchData.CONTNO,
        },
        { headers: HEADERS_EXPORT_BEN },
      );
      if (res?.data) {
        setBillPayment(res?.data);
        setIsModalPayIn(true);
      }
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error loadMemoDataOld:", error);
        message.error("เกิดข้อผิดพลาดในการโหลดข้อมูลสัญญา");
      }
    } finally {
      setLoadingLoan(false);
    }
  };

  const loadDuepayment = async (api, payload) => {
    if (duePaymentData?.length > 0) return;

    setLoadingLoan(true);
    try {
      const res = await axios.post(api, payload, {
        headers: HEADERS_EXPORT_BEN,
      });
      if (res?.data) {
        console.log(res);
        setDuePaymentData(res.data.data);
      }
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error loadMemoDataOld:", error);
        message.error("เกิดข้อผิดพลาดในการโหลดข้อมูลสัญญา");
      }
    } finally {
      setLoadingLoan(false);
    }
  };

  const loadFollowCustomer = async () => {
    if (followCusData?.length > 0) return;

    setLoadingLoan(true);
    try {
      const res = await axios.post(
        POST_FOLLOW_CUSTOMER,
        {
          idJudge: judgeData?.judge?.[0]?.idJudge,
        },
        { headers: HEADERS_EXPORT_BEN },
      );
      if (res?.data) {
        console.log(res);
        setFollowCusData(res.data.data);
      }
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error loadMemoDataOld:", error);
        message.error("เกิดข้อผิดพลาดในการโหลดข้อมูลสัญญา");
      }
    } finally {
      setLoadingLoan(false);
    }
  };

  const loadImge = async (force = false) => {
    // ✅ skip ถ้าไม่ได้ force และมีข้อมูลอยู่แล้ว
    if (!force && fileListByContractCourt.length > 0) return;

    setLoadingImge(true);

    try {
      const loanId = judgeData?.judge?.[0]?.loanId;
      const menuTypes = getMenuTypesToLoad(judgeData);
      const allowed = getAllowedTypes(judgeData);

      if (!loanId || menuTypes.length === 0) {
        message.warning("ไม่มีเงื่อนไขโหลดเอกสาร");
        setFileListByContractCourt([]);
        return;
      }

      const responses = await Promise.all(
        menuTypes.map((menuType) =>
          axios.get(`${GET_LOAD_IMAGE_NEW_ALL}/${menuType}/${loanId}`, {
            headers: HEADERS_UPIMAGE,
          }),
        ),
      );

      const mapImg = (img) => ({
        ...img,
        CONTNO: judgeData?.judge?.[0]?.CONTNO,
        loanId: judgeData?.judge?.[0]?.loanId,
        lawsuitId: judgeData?.judge?.[0]?.lawsuitId,
      });

      const allImages = responses
        .flatMap((res) => (res.data || []).map(mapImg))
        .filter((img) =>
          allowed.some(
            (a) => a.menuType === img.menuType && a.imageType === img.imageType,
          ),
        );

      if (allImages.length === 0) message.warning("ไม่พบเอกสาร");

      setFileListByContractCourt(allImages);
    } catch (error) {
      console.error("Error loading data:", error);
      message.error(`เกิดข้อผิดพลาด: ${error.message}`);
      setFileListByContractCourt([]);
    } finally {
      setLoadingImge(false);
    }
  };

  const postMemoNew = async () => {
    if (!remarkValueNew) {
      message.warning("กรุณากรอกหมายเหตุก่อนบันทึก");
      return;
    }
    const payload = {
      lawsuitId: judgeData.judge?.[0].lawsuitId,
      note: remarkValueNew,
      userId: userId,
    };
    setLoadingLoan(true);
    try {
      const res = await axios.post(POST_INSERT_NOTE_NEW, payload, {
        headers: HEADERS_EXPORT_BEN,
      });
      if (res?.status === 200) {
        message.success("บันทึกหมายเหตุสำเร็จ");
        setRemarkValueNew("");
        await loadJudgeData(searchData); // ✅ reload note list
      }
      return res;
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error postMemoNew:", error);
        message.error("เกิดข้อผิดพลาดในการบันทึกหมายเหตุ");
      }
    } finally {
      setLoadingLoan(false);
    }
  };

  const postMemoOld = async () => {
    const payload = {
      DATA_TYPE: memoDataOld.DATA_TYPE,
      CONTNO: memoDataOld.CONTNO,
      MEMO1: memoDataOld.MEMO1,
    };

    setLoadingJudge(true);
    try {
      const res = await axios.post(POST_UPDATE_MEMO, payload, {
        headers: HEADERS_EXPORT_BEN,
      });
      if (res?.status === 200) {
        message.success("บันทึกหมายเหตุสำเร็จ");
        setEditMemoOld(true);
        await loadMemoDataOld(memoDataOld.CONTNO);
      }
      return res?.data;
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message;
      if (status === 404) message.error("ไม่พบปลายทาง (404)");
      else if (status === 400) message.error(`ข้อมูลไม่ถูกต้อง: ${serverMsg}`);
      else if (status === 500)
        message.error("เกิดข้อผิดพลาดจาก Server ! จำนวนตัวอักษรเยอะเกินไป");
      else
        message.error(
          `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
        );
      console.error("postMemoOld error:", error);
    } finally {
      setLoadingJudge(false);
    }
  };

  const loadDataBeeypup = async (api, payload) => {
    if (beeypupData?.length > 0) return;

    setLoadBeeypup(true);

    try {
      const res = await axios.post(api, payload, {
        headers: HEADERS_EXPORT_BEN,
      });
      if (res.status === 200) {
        setBeeypupData(
          Array.isArray(res.data) ? res.data : res.data?.data || [],
        );
      }
    } catch (err) {
      message.error(
        err?.response?.status === 404 ? "ไม่พบข้อมูล" : "เกิดข้อผิดพลาด",
      );
    } finally {
      setLoadBeeypup(false);
    }
  };

  const postApporve = async (payload) => {
    setLoadApprovDue(true);
    try {
      const res = await axios.post(POST_APPROVED_DUE, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      const { status, data } = res;

      if (status === 200) {
        console.log("✅ API Response:", data);
        return data;
      }
    } catch (error) {
      const status = error?.response?.status;
      const errData = error?.response?.data;
      const serverMsg = errData?.error || errData?.message || error.message;

      if (status === 404) {
        message.error("ไม่พบปลายทาง (404)");
      } else if (status === 400) {
        message.error(serverMsg);
      } else if (status === 500) {
        message.error("เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ (500)");
      } else {
        message.error(
          `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
        );
      }
      console.error("❌ postDocStatus error:", error);
      throw error;
    }
  };

  // แยก error handler ใช้ซ้ำได้หลายที่
  const handleApiError = (error, ctx = "getEmsData") => {
    const status = error?.response?.status;
    const serverMsg =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message;
    if (status === 404) message.error("ไม่พบปลายทาง (404)");
    else if (status === 400) message.error(`ข้อมูลไม่ถูกต้อง: ${serverMsg}`);
    else if (status === 500)
      message.error("เกิดข้อผิดพลาดจาก Server ! จำนวนตัวอักษรเยอะเกินไป");
    else
      message.error(
        `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
      );
    console.error(`${ctx} error:`, error);
  };

  const getEmsData = async () => {
    const contno = (searchData?.CONTNO ?? "").trim().replace(/^V/i, "");

    setLoadingJudge(true);
    try {
      // ยิงพร้อมกัน — อิสระต่อกัน ไม่ต้องรอทีละตัว
      const [statusRes, emsRes] = await Promise.allSettled([
        axios.get(GET_STATUS_THAIPOST, { headers: HEADERS_EXPORT_BEN }),
        contno
          ? axios.get(`${GET_DATA_EMS}${encodeURIComponent(contno)}`, {
              headers: HEADERS_EXPORT_BEN,
            })
          : Promise.resolve(null),
      ]);

      // 1) สถานะ EMS (master)
      if (statusRes.status === "fulfilled") {
        if (statusRes.value?.status === 200) setStatusEms(statusRes.value.data);
      } else {
        handleApiError(statusRes.reason, "GET_STATUS_THAIPOST");
      }

      // 2) ข้อมูล EMS ตามสัญญา
      if (emsRes.status === "fulfilled") {
        if (emsRes.value?.status === 200) setMailData(emsRes.value.data);
      } else {
        handleApiError(emsRes.reason, "GET_DATA_EMS");
      }

      return statusRes.status === "fulfilled" ? statusRes.value?.data : null;
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoadingJudge(false);
    }
  };

  // ── Tab change handler ────────────────────────────────────────────────────
  const handleTabChange = (key) => {
    setActiveTab(key);
    // ถ้ากด tab 4 หรือ 5 และยังไม่มี loanData → โหลด
    if ((key === "6" || key === "7") && searchData?.CONTNO && !loanData) {
      console.log("judgeData?.judge?.[0]?.CONTNO", judgeData);

      if (judgeData?.judge?.[0]?.CONTNO) {
        loadLoanData(judgeData?.judge[0]?.CONTNO);
      } else {
        loadLoanData(searchData.CONTNO);
      }
    }
    if ((key === "8" || key === "6") && !memoDataOld) {
      if (judgeData?.judge?.[0]?.CONTNO) {
        loadMemoDataOld(judgeData?.judge?.[0]?.CONTNO);
      } else {
        loadMemoDataOld(searchData.CONTNO);
      }
    }

    if (key === "4") {
      let apiUseDue;
      let payloadDue;
      let apiUseBeeypup;
      let payloadBeeypup;

      if (judgeData?.loanNew !== null) {
        console.log("judgeData?.loanNew");

        apiUseDue = POST_LOAD_DATA_DUEPAYMENT;
        payloadDue = {
          loanNewId: judgeData?.loanNew?.LoanNewId,
        };
        apiUseBeeypup = POST_PREVIEW_BEEYPUP;
        payloadBeeypup = {
          NEW_CONTNO: judgeData?.loanNew?.NEWCONTNO,
          loanNewId: judgeData?.loanNew?.LoanNewId,
          DATETIME: dayjs().format("YYYY-MM-DDTHH:mm:ssZ"),
        };

        loadDuepayment(apiUseDue, payloadDue);
        loadDataBeeypup(apiUseBeeypup, payloadBeeypup);
      } else if (judgeData?.loanJudge !== null) {
        console.log("judgeData?.loanJudge");

        apiUseDue = POST_LOAD_DATA_DUEPAYMENT_JUDGE;
        payloadDue = {
          loanJudgeId: judgeData?.loanJudge?.loanJudgeId,
        };

        apiUseBeeypup = POST_JUDGE_BEEYPUP;
        payloadBeeypup = {
          NEW_CONTNO: judgeData?.loanJudge?.NEW_CONTNO,
          loanJudgeId: judgeData?.loanJudge?.loanJudgeId,
          DATETIME: dayjs().format("YYYY-MM-DDTHH:mm:ssZ"),
        };

        loadDuepayment(apiUseDue, payloadDue);
        loadDataBeeypup(apiUseBeeypup, payloadBeeypup);
      }
    }

    if (key === "2" && !loanData) {
      if (judgeData?.judge?.[0]?.CONTNO) {
        loadLoanData(judgeData?.judge[0]?.CONTNO);
      }
    }

    if (key === "9") {
      loadImge();
    }

    if (key === "10") {
      loadFollowCustomer();
    }

    if (key === "13") {
      getEmsData();
    }
  };

  useEffect(() => {
    return () => clearSearch();
  }, []);

  // ==================== RENDER: HEADER ====================
  const renderContractHeader = () => {
    const loan = loanData?.LOAN;
    const customer = loanData?.CUSTOMER;

    const FLAG_CONFIG = {
      1: {
        background: "#f6ffed",
        border: "1px solid #b7eb8f",
        label: "ทำยอม",
        badgeColor: "#389e0d",
        badgeBg: "#d9f7be",
      },
      2: {
        background: "#fff2f0",
        border: "1px solid #ffccc7",
        label: "พิพากษา",
        badgeColor: "#cf1322",
        badgeBg: "#ffebe8",
      },
      6: {
        background: "#fff2f0",
        border: "1px solid #ffccc7",
        label: "(ส่วนต่าง)",
        badgeColor: "#cf1322",
        badgeBg: "#ffebe8",
      },
    };

    const flagStyle = judgeData?.flagLoanNew
      ? (FLAG_CONFIG[judgeData?.flagLoanNew] ?? FLAG_CONFIG[1])
      : "";
    const lawStyle = judgeData?.judge?.[0]?.LAW_TYPE_ID === 6 && FLAG_CONFIG[6];

    return (
      <div
        style={{
          background: THEME.gradient,
          padding: "24px 32px",
          borderRadius: THEME.borderRadius,
          marginBottom: 24,
          color: "#fff",
        }}
      >
        <Row gutter={[24, 16]} align="middle">
          <Col xs={24} md={16}>
            <Space direction="vertical" size={4}>
              <Title level={3} style={{ color: "#fff", margin: "8px 0" }}>
                <FileTextOutlined style={{ marginRight: 12 }} />
                {loan?.CONTNO || memoDataOld?.CONTNO}
                {(judgeData?.loanNew?.NEWCONTNO ||
                  judgeData?.judge?.[0]?.newContno) &&
                  " ➡️ " +
                    (judgeData?.loanNew?.NEWCONTNO ||
                      judgeData?.judge?.[0]?.newContno)}{" "}
                <span
                  style={{
                    background: flagStyle.badgeBg,
                    color: flagStyle.badgeColor,
                    borderRadius: 20,
                    padding: "2px 10px",
                    fontSize: 20,
                    fontWeight: 500,
                  }}
                >
                  {" "}
                  {flagStyle.label}
                  {lawStyle.label}
                </span>
              </Title>
              <Space size="large">
                <Text copyable style={{ color: "rgba(255,255,255,0.85)" }}>
                  <UserOutlined style={{ marginRight: 6 }} />
                  {customer?.SNAM}
                  {customer?.NAME1} {customer?.NAME2}
                </Text>
                <Text copyable style={{ color: "rgba(255,255,255,0.85)" }}>
                  <IdcardOutlined style={{ marginRight: 6 }} />
                  {customer?.CUSCOD}
                </Text>
              </Space>
            </Space>
          </Col>

          <Col xs={24} md={8} style={{ textAlign: "right" }}>
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.7)" }}>
                  สถานะสัญญา
                </Text>
              }
              value={
                judgeData?.ACTIVE || judgeData?.judge?.length > 0
                  ? judgeData?.judge
                    ? `${judgeData.judge?.[0]?.statusDescription} ${judgeData.judge?.[0]?.currentStatus > 10 ? (judgeData.judge?.[0]?.SDATE ? "(เป็นสัญญาแล้ว)" : "(ยังไม่อนุมัติสัญญา)") : ""} `
                    : judgeData?.statusDescription || "ไม่มีสถานะ"
                  : "ปิดสัญญา"
              }
              valueStyle={{ color: "#fff", fontSize: 28 }}
            />
          </Col>
        </Row>
      </div>
    );
  };

  // ==================== TAB 1: ข้อมูลหลังพิพากษา ====================
  const renderJudgementInfo = () => {
    const judges = judgeData?.judge || [];

    if (judges?.length === 0) {
      return (
        <StyledCard>
          <Empty description="ยังไม่มีข้อมูลหลังพิพากษา" />
        </StyledCard>
      );
    }

    const groupedJudges = judges?.reduce((acc, judge) => {
      const key = judge.judgeNumber;
      if (!acc[key]) acc[key] = [];
      acc[key].push(judge);
      return acc;
    }, {});

    const judgeNumbers = Object.keys(groupedJudges).sort((a, b) => a - b);

    const renderJudgeContent = (judgeNum) => {
      const judgesInGroup = groupedJudges[judgeNum];
      const firstJudge = judgesInGroup?.[0];

      const totalJudgement =
        Number(firstJudge?.judgement || 0) +
        Number(firstJudge?.AROTHR || 0) +
        Number(firstJudge?.costOfUselessness || 0);

      const totalPaid = (judgeData?.chqtran ?? [])
        ?.filter((r) => r.FLAG !== "C")
        .reduce((sum, item) => sum + parseFloat(item.PAYAMT_N || 0), 0);

      const totalPaidArothr = (judgeData?.chqtran_arothr ?? [])
        ?.filter((r) => r.FLAG !== "C")
        .reduce((sum, item) => sum + parseFloat(item.PAYAMT || 0), 0);

      const totalPaidAll = parseFloat(totalPaid + totalPaidArothr);
      const paymentProgress =
        totalJudgement > 0 ? (totalPaidAll / totalJudgement) * 100 : 0;

      return (
        <div>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <div
                style={{
                  textAlign: "center",
                  padding: 20,
                  background: `${THEME.danger}10`,
                  borderRadius: 10,
                  height: "100%",
                }}
              >
                <Text type="secondary" style={{ fontSize: 16 }}>
                  ค่าชดใช้แทน
                </Text>
                <Title
                  level={3}
                  style={{ margin: "8px 0", color: THEME.danger }}
                >
                  {formatMoney(firstJudge?.judgement)}
                </Title>
                <Space direction="vertical" size={0}>
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    ต้นเงิน: {formatMoney(firstJudge?.judgementLack)}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    ดอกเบี้ย: {firstJudge?.interestRate || 0}% /ปี
                  </Text>
                </Space>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div
                style={{
                  textAlign: "center",
                  padding: 20,
                  background: `${THEME.warning}10`,
                  borderRadius: 10,
                  height: "100%",
                }}
              >
                <Text type="secondary" style={{ fontSize: 16 }}>
                  ค่าขาดประโยชน์
                </Text>
                <Title
                  level={3}
                  style={{ margin: "8px 0", color: THEME.warning }}
                >
                  {formatMoney(firstJudge?.lackOfBenefits)}
                </Title>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  ดอกเบี้ย: {firstJudge?.costRate || 0}% /ปี
                </Text>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div
                style={{
                  textAlign: "center",
                  padding: 20,
                  background: `${THEME.info}10`,
                  borderRadius: 10,
                  height: "100%",
                }}
              >
                <Text type="secondary" style={{ fontSize: 16 }}>
                  ลูกหนี้อื่นๆ
                </Text>
                <Title level={3} style={{ margin: "8px 0", color: THEME.info }}>
                  {formatMoney(firstJudge?.AROTHR)}
                </Title>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  บาท
                </Text>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div
                style={{
                  textAlign: "center",
                  padding: 20,
                  background: `${THEME.accent}10`,
                  borderRadius: 10,
                  height: "100%",
                }}
              >
                <Text type="secondary" style={{ fontSize: 16 }}>
                  ดอกเบี้ยก่อนฟ้อง
                </Text>
                <Title
                  level={3}
                  style={{ margin: "8px 0", color: THEME.accent }}
                >
                  {formatMoney(firstJudge?.judgementInterest)}
                </Title>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  บาท
                </Text>
              </div>
            </Col>
          </Row>

          <StyledCard style={{ marginBottom: 16 }}>
            <Row gutter={[24, 16]}>
              <Col xs={24} md={12}>
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, #f6ffed 0%, #fff 100%)",
                    border: "1px solid #b7eb8f",
                    borderRadius: 12,
                    padding: "16px 20px",
                  }}
                >
                  <Space style={{ marginBottom: 10 }}>
                    <span style={{ fontSize: 18 }}>💳</span>
                    <Text strong style={{ fontSize: 16 }}>
                      ความคืบหน้าการชำระ
                    </Text>
                    <Tag
                      color={paymentProgress >= 100 ? "success" : "processing"}
                      style={{ borderRadius: 10 }}
                    >
                      {Number(paymentProgress ? paymentProgress.toFixed(1) : 0)}
                      %
                    </Tag>
                  </Space>
                  <Progress
                    percent={Number(
                      paymentProgress ? paymentProgress.toFixed(1) : 0,
                    )}
                    strokeColor={{ "0%": THEME.success, "100%": "#52c41a" }}
                    trailColor="#e6f7e6"
                    size={{ width: "100%", height: 12 }}
                    style={{ marginBottom: 12 }}
                  />
                  <Row gutter={[8, 0]}>
                    <Col span={12}>
                      <div
                        style={{
                          background: "#fff",
                          border: "1px solid #f0f0f0",
                          borderRadius: 8,
                          padding: "6px 12px",
                          textAlign: "center",
                        }}
                      >
                        <Text
                          type="secondary"
                          style={{ fontSize: 16, display: "block" }}
                        >
                          ชำระแล้ว
                        </Text>
                        <Text
                          strong
                          style={{ color: THEME.success, fontSize: 18 }}
                        >
                          {formatMoney(totalPaidAll)}{" "}
                          <span
                            style={{
                              color: "#aaa",
                              fontSize: 14,
                              fontWeight: 400,
                            }}
                          >
                            บาท
                          </span>
                        </Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div
                        style={{
                          background: "#fff",
                          border: "1px solid #f0f0f0",
                          borderRadius: 8,
                          padding: "6px 12px",
                          textAlign: "center",
                        }}
                      >
                        <Text
                          type="secondary"
                          style={{ fontSize: 16, display: "block" }}
                        >
                          คงเหลือ
                        </Text>
                        <Text
                          strong
                          style={{ color: THEME.danger, fontSize: 18 }}
                        >
                          {formatMoney(totalJudgement - totalPaidAll)}{" "}
                          <span
                            style={{
                              color: "#aaa",
                              fontSize: 14,
                              fontWeight: 400,
                            }}
                          >
                            บาท
                          </span>
                        </Text>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, #e6f4ff 0%, #fff 100%)",
                    border: "1px solid #91caff",
                    borderRadius: 12,
                    padding: "16px 20px",
                    height: "100%",
                  }}
                >
                  <Space style={{ marginBottom: 12 }}>
                    <span style={{ fontSize: 18 }}>📅</span>
                    <Text strong style={{ fontSize: 16 }}>
                      ข้อมูลดอกเบี้ย
                    </Text>
                  </Space>
                  <Row gutter={[10, 10]}>
                    {[
                      {
                        icon: "📌",
                        label: "วันเริ่มคิดดอกเบี้ย (เงินต้น)",
                        value: formatDate(firstJudge?.firstDate),
                        color: THEME.primary,
                      },
                      {
                        icon: "📌",
                        label: "วันเริ่มคิดดอกเบี้ย (ค่าขาดประโยชน์)",
                        value: formatDate(firstJudge?.costFirstDate),
                        color: THEME.warning,
                      },
                      {
                        icon: "📊",
                        label: "อัตราดอกเบี้ย (เงินต้น)",
                        value: `${firstJudge?.interestRate || 0}% ต่อปี`,
                        color: THEME.primary,
                      },
                      {
                        icon: "📊",
                        label: "อัตราดอกเบี้ย (ค่าขาดประโยชน์)",
                        value: `${firstJudge?.costRate || 0}% ต่อปี`,
                        color: THEME.warning,
                      },
                    ].map(({ icon, label, value, color }) => (
                      <Col span={12} key={label}>
                        <div
                          style={{
                            background: "#fff",
                            border: "1px solid #f0f0f0",
                            borderRadius: 8,
                            padding: "8px 10px",
                            height: "100%",
                          }}
                        >
                          <Text
                            type="secondary"
                            style={{
                              fontSize: 16,
                              display: "block",
                              marginBottom: 2,
                            }}
                          >
                            {icon} {label}
                          </Text>
                          <Text strong style={{ color, fontSize: 18 }}>
                            {value || "-"}
                          </Text>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </Col>
            </Row>
          </StyledCard>

          {judgeData?.chqtran?.length > 0 && (
            <>
              {judgeData?.loanNew ? (
                <StyledCard
                  title="ตารางการรับชำระรวม(ทำยอม)"
                  icon={
                    <DollarOutlined
                      style={{ color: THEME.primary, fontSize: 18 }}
                    />
                  }
                  style={{ marginBottom: 16 }}
                >
                  <Table
                    dataSource={judgeData.chqtran}
                    columns={[
                      {
                        title: "ลำดับ",
                        key: "index",
                        width: 60,
                        align: "center",
                        render: (_, __, idx) => idx + 1,
                      },
                      {
                        title: "วันที่ชำระ",
                        dataIndex: "PAYDT",
                        key: "PAYDT",
                        render: (val) => formatDate(val),
                      },
                      {
                        title: "ชื่อ-นามสกุล",
                        key: "name",
                        render: (_, r) => (
                          <Text>
                            {r.SNAM}
                            {r.NAME1} {r.NAME2}
                          </Text>
                        ),
                      },
                      { title: "รายการ", dataIndex: "FORDESC", key: "FORDESC" },
                      {
                        title: "เลขที่ใบเสร็จ",
                        dataIndex: "TMBILL",
                        key: "TMBILL",
                        render: (val) => (
                          <Text style={{ fontFamily: "monospace" }}>{val}</Text>
                        ),
                      },
                      {
                        title: "จำนวนเงิน",
                        dataIndex: "NETPAY",
                        key: "NETPAY",
                        align: "right",
                        render: (val) => (
                          <Text strong style={{ color: THEME.success }}>
                            {formatMoney(val)} บาท
                          </Text>
                        ),
                      },
                      {
                        title: "ผู้รับ",
                        dataIndex: "INPUT_BY",
                        key: "INPUT_BY",
                        align: "center",
                        render: (val) => <Tag color="blue">{val}</Tag>,
                      },
                    ]}
                    rowKey="wctId"
                    pagination={{
                      defaultPageSize: 5,
                      showSizeChanger: true,
                      pageSizeOptions: ["5", "10", "20", "50", "100"],
                    }}
                    size="middle"
                    scroll={{ x: 800 }}
                    summary={() => {
                      const totalNetpay = judgeData?.chqtran?.reduce(
                        (sum, r) => sum + Number(r.NETPAY || 0),
                        0,
                      );
                      return (
                        <Table.Summary.Row style={{ background: THEME.light }}>
                          <Table.Summary.Cell colSpan={4}>
                            <Text strong>รวมทั้งหมด</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong style={{ color: THEME.success }}>
                              {formatMoney(totalNetpay)} บาท
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell />
                        </Table.Summary.Row>
                      );
                    }}
                  />
                </StyledCard>
              ) : (
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  {judgeData?.chqtran?.filter(
                    (item) => item.FORDESC === "ค่าแทน",
                  )?.length > 0 && (
                    <Col xs={24} lg={12}>
                      <StyledCard
                        title="ตารางการรับชำระรวม - ค่าแทน"
                        icon={
                          <DollarOutlined
                            style={{ color: THEME.danger, fontSize: 18 }}
                          />
                        }
                        style={{ height: "100%" }}
                      >
                        <Table
                          dataSource={judgeData?.chqtran?.filter(
                            (item) => item.FORDESC === "ค่าแทน",
                          )}
                          columns={[
                            {
                              title: "#",
                              key: "index",
                              width: 10,
                              align: "center",
                              render: (_, __, idx) => idx + 1,
                            },
                            {
                              title: "วันที่ชำระ",
                              dataIndex: "PAYDT",
                              key: "PAYDT",
                              width: 40,
                              render: (val) => formatDate(val),
                            },
                            {
                              title: "ชื่อ-นามสกุล",
                              key: "name",
                              ellipsis: true,
                              width: 70,
                              render: (_, r) => (
                                <Text
                                  style={{
                                    fontFamily: "monospace",
                                    fontSize: 14,
                                  }}
                                >
                                  {r.SNAM}
                                  {r.NAME1} {r.NAME2}
                                </Text>
                              ),
                            },
                            {
                              title: "ใบเสร็จ",
                              dataIndex: "TMBILL",
                              key: "TMBILL",
                              width: 50,
                              render: (val) => (
                                <Text
                                  style={{
                                    fontFamily: "monospace",
                                    fontSize: 14,
                                  }}
                                >
                                  {val}
                                </Text>
                              ),
                            },
                            {
                              title: "จำนวนเงิน",
                              dataIndex: "NETPAY",
                              key: "NETPAY",
                              width: 40,
                              align: "right",
                              render: (val, record) => (
                                <Text
                                  style={{
                                    color:
                                      record.FLAG === "C" ? "red" : "green",
                                  }}
                                >
                                  {formatMoney(val)}
                                </Text>
                              ),
                            },
                            {
                              title: "สถานะ",
                              dataIndex: "FLAG",
                              key: "FLAG",
                              width: 40,
                              align: "right",
                              render: (val) => (
                                <Tag color={val === "C" ? "red" : "green"}>
                                  {val === "C" ? "ยกเลิก" : "รับปกติ"}
                                </Tag>
                              ),
                            },
                            {
                              title: "ผู้รับ",
                              dataIndex: "INPUT_BY",
                              key: "INPUT_BY",
                              align: "center",
                              width: 40,
                              render: (val) => <Tag color="blue">{val}</Tag>,
                            },
                          ]}
                          rowKey="wctId"
                          pagination={{
                            defaultPageSize: 5,
                            showSizeChanger: true,
                          }}
                          size="small"
                          summary={() => {
                            const total = judgeData?.chqtran
                              ?.filter((r) => r.FLAG !== "C")
                              .reduce(
                                (sum, r) => sum + Number(r.NETPAY || 0),
                                0,
                              );
                            return (
                              <Table.Summary.Row
                                style={{ background: THEME.light }}
                              >
                                <Table.Summary.Cell colSpan={4}>
                                  <Text strong>รวมชำระ</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell align="right">
                                  <Text
                                    strong
                                    style={{
                                      color: THEME.success,
                                      fontSize: 16,
                                    }}
                                  >
                                    {formatMoney(total)}
                                  </Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell />
                              </Table.Summary.Row>
                            );
                          }}
                        />
                      </StyledCard>
                    </Col>
                  )}
                  {judgeData?.chqtran?.filter(
                    (item) => item.FORDESC === "ค่าขาดประโยชน์",
                  )?.length > 0 && (
                    <Col xs={24} lg={12}>
                      <StyledCard
                        title="ตารางการรับชำระรวม - ค่าขาดประโยชน์"
                        icon={
                          <DollarOutlined
                            style={{ color: THEME.warning, fontSize: 18 }}
                          />
                        }
                        style={{ height: "100%" }}
                      >
                        <Table
                          dataSource={judgeData?.chqtran?.filter(
                            (item) => item.FORDESC === "ค่าขาดประโยชน์",
                          )}
                          columns={[
                            {
                              title: "#",
                              key: "index",
                              width: 10,
                              align: "center",
                              render: (_, __, idx) => idx + 1,
                            },
                            {
                              title: "วันที่ชำระ",
                              dataIndex: "PAYDT",
                              key: "PAYDT",
                              width: 40,
                              render: (val) => formatDate(val),
                            },
                            {
                              title: "ชื่อ-นามสกุล",
                              key: "name",
                              width: 70,
                              ellipsis: true,
                              render: (_, r) => (
                                <Text
                                  style={{
                                    fontFamily: "monospace",
                                    fontSize: 14,
                                  }}
                                >
                                  {r.SNAM}
                                  {r.NAME1} {r.NAME2}
                                </Text>
                              ),
                            },
                            {
                              title: "ใบเสร็จ",
                              dataIndex: "TMBILL",
                              key: "TMBILL",
                              width: 50,
                              render: (val) => (
                                <Text
                                  style={{
                                    fontFamily: "monospace",
                                    fontSize: 14,
                                  }}
                                >
                                  {val}
                                </Text>
                              ),
                            },
                            {
                              title: "จำนวนเงิน",
                              dataIndex: "NETPAY",
                              key: "NETPAY",
                              width: 40,
                              align: "right",
                              render: (val, record) => (
                                <Text
                                  style={{
                                    color:
                                      record.FLAG === "C" ? "red" : "green",
                                  }}
                                >
                                  {formatMoney(val)}
                                </Text>
                              ),
                            },
                            {
                              title: "สถานะ",
                              dataIndex: "FLAG",
                              key: "FLAG",
                              width: 40,
                              align: "right",
                              render: (val) => (
                                <Tag color={val === "C" ? "red" : "green"}>
                                  {val === "C" ? "ยกเลิก" : "รับปกติ"}
                                </Tag>
                              ),
                            },
                            {
                              title: "ผู้รับ",
                              dataIndex: "INPUT_BY",
                              key: "INPUT_BY",
                              align: "center",
                              width: 50,
                              render: (val) => <Tag color="blue">{val}</Tag>,
                            },
                          ]}
                          rowKey="wctId"
                          pagination={{
                            defaultPageSize: 5,
                            showSizeChanger: true,
                          }}
                          size="small"
                          summary={() => {
                            const total = judgeData?.chqtran
                              ?.filter((r) => r.FLAG !== "C")
                              .reduce(
                                (sum, r) => sum + Number(r.NETPAY || 0),
                                0,
                              );
                            return (
                              <Table.Summary.Row
                                style={{ background: THEME.light }}
                              >
                                <Table.Summary.Cell colSpan={4}>
                                  <Text strong>รวมชำระ</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell align="right">
                                  <Text
                                    strong
                                    style={{
                                      color: THEME.success,
                                      fontSize: 16,
                                    }}
                                  >
                                    {formatMoney(total)}
                                  </Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell />
                              </Table.Summary.Row>
                            );
                          }}
                        />
                      </StyledCard>
                    </Col>
                  )}
                </Row>
              )}
            </>
          )}

          {judgeData?.chqtran_arothr?.length > 0 && (
            <>
              <Row style={{ marginBottom: 16 }}>
                <Col>
                  <StyledCard
                    title="ตารางการรับลูกหนี้อื่น ๆ"
                    icon={
                      <DollarOutlined
                        style={{ color: THEME.danger, fontSize: 18 }}
                      />
                    }
                    style={{ height: "100%" }}
                  >
                    <Table
                      dataSource={judgeData?.chqtran_arothr}
                      columns={[
                        {
                          title: "#",
                          key: "index",
                          width: 10,
                          align: "center",
                          render: (_, __, idx) => idx + 1,
                        },
                        {
                          title: "วันที่ชำระ",
                          dataIndex: "PAYDT",
                          key: "PAYDT",
                          width: 40,
                          render: (val) => formatDate(val),
                        },
                        {
                          title: "ชื่อ-นามสกุล",
                          key: "name",
                          ellipsis: true,
                          width: 70,
                          render: (_, r) => (
                            <Text
                              style={{
                                fontFamily: "monospace",
                                fontSize: 14,
                              }}
                            >
                              {r.SNAM}
                              {r.NAME1} {r.NAME2}
                            </Text>
                          ),
                        },
                        {
                          title: "ใบเสร็จ",
                          dataIndex: "TMBILL",
                          key: "TMBILL",
                          width: 30,
                          render: (val) => (
                            <Text
                              style={{
                                fontFamily: "monospace",
                                fontSize: 14,
                              }}
                            >
                              {val}
                            </Text>
                          ),
                        },
                        {
                          title: "ประเภทจ่าย",
                          dataIndex: "PAYTYP",
                          key: "PAYTYP",
                          width: 50,
                          align: "center",
                          render: (val) => (
                            <Text
                              style={{
                                fontFamily: "monospace",
                                fontSize: 14,
                              }}
                            >
                              {val === "01" ? "เงินสด" : "โอน"}
                            </Text>
                          ),
                        },
                        {
                          title: "ค่า",
                          dataIndex: "FORDESC",
                          key: "FORDESC",
                          width: 30,
                          align: "center",
                          render: (val) => (
                            <Text
                              style={{
                                fontFamily: "monospace",
                                fontSize: 14,
                              }}
                            >
                              {val}
                            </Text>
                          ),
                        },
                        {
                          title: "จำนวนเงิน",
                          dataIndex: "NETPAY",
                          key: "NETPAY",
                          width: 40,
                          align: "right",
                          render: (val, record) => (
                            <Text
                              style={{
                                color: record.FLAG === "C" ? "red" : "green",
                              }}
                            >
                              {formatMoney(val)}
                            </Text>
                          ),
                        },
                        {
                          title: "สถานะ",
                          dataIndex: "FLAG",
                          key: "FLAG",
                          width: 40,
                          align: "right",
                          render: (val) => (
                            <Tag color={val === "C" ? "red" : "green"}>
                              {val === "C" ? "ยกเลิก" : "รับปกติ"}
                            </Tag>
                          ),
                        },
                        {
                          title: "ผู้รับ",
                          dataIndex: "INPUT_BY",
                          key: "INPUT_BY",
                          align: "center",
                          width: 40,
                          render: (val) => <Tag color="blue">{val}</Tag>,
                        },
                      ]}
                      rowKey="wctId"
                      pagination={{
                        defaultPageSize: 5,
                        showSizeChanger: true,
                      }}
                      size="small"
                      summary={() => {
                        const total = judgeData?.chqtran_arothr
                          ?.filter((r) => r.FLAG !== "C")
                          .reduce((sum, r) => sum + Number(r.NETPAY || 0), 0);

                        return (
                          <Table.Summary.Row
                            style={{ background: THEME.light }}
                          >
                            <Table.Summary.Cell colSpan={6}>
                              <Text strong>รวมชำระ</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell align="right">
                              <Text
                                strong
                                style={{
                                  color: THEME.success,
                                  fontSize: 16,
                                }}
                              >
                                {formatMoney(total)}
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell colSpan={2} />
                          </Table.Summary.Row>
                        );
                      }}
                    />
                  </StyledCard>
                </Col>
              </Row>
            </>
          )}

          <StyledCard
            title={`รายละเอียดจำเลยในคำพิพากษานี้ (${judgesInGroup?.length} คน)`}
            icon={
              <TeamOutlined style={{ color: THEME.primary, fontSize: 18 }} />
            }
            style={{ padding: 10 }}
          >
            <Tabs
              type="card"
              items={judgesInGroup?.map((defendant) => {
                // 1. หาข้อมูลจาก array cal ที่ CUSTOMER_ID ตรงกับจำเลยคนนี้
                return {
                  key: String(defendant?.idJudgeDef),
                  label: (
                    <Space>
                      <Avatar
                        size={24}
                        style={{ background: THEME.gradient, fontSize: 14 }}
                      >
                        {defendant?.defendantNumber}
                      </Avatar>
                      {defendant?.SNAM}
                      {defendant?.NAME1} {defendant?.NAME2}
                    </Space>
                  ),
                  children: (
                    <div
                      style={{
                        padding: 20,
                        background: THEME.light,
                        borderRadius: 10,
                        borderLeft: `4px solid ${THEME.primary}`,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          marginBottom: 16,
                        }}
                      >
                        <Avatar
                          size={56}
                          style={{ background: THEME.gradient }}
                        >
                          {defendant?.defendantNumber}
                        </Avatar>
                        <div style={{ flex: 1 }}>
                          <Text strong style={{ fontSize: 18 }}>
                            {defendant?.SNAM}
                            {defendant?.NAME1} {defendant?.NAME2}
                          </Text>
                          <br />
                          <Space size={4}>
                            <Tag color="geekblue">
                              คำพิพากษาที่ {defendant?.judgeNumber}
                            </Tag>
                            <Tag color="blue">
                              จำเลยที่ {defendant?.defendantNumber}
                            </Tag>
                            <Tag
                              color={
                                judgeData?.flagLoanNew === 1
                                  ? "green"
                                  : "orange"
                              }
                            >
                              {judgeData?.flagLoanNew === 1
                                ? "ทำยอม"
                                : "พิพากษา"}
                            </Tag>
                          </Space>
                        </div>
                      </div>
                      <Row gutter={[12, 8]}>
                        <Col span={24}>
                          <Text type="secondary" style={{ fontSize: 16 }}>
                            <IdcardOutlined style={{ marginRight: 6 }} />
                            เลขบัตรประชาชน/ทะเบียนนิติบุคคล
                          </Text>
                          <br />
                          <Text
                            copyable
                            strong
                            style={{ fontFamily: "monospace", fontSize: 18 }}
                          >
                            {defendant?.CUSCOD}
                          </Text>
                        </Col>
                      </Row>
                      <Divider style={{ margin: "12px 0" }} />
                      <Row
                        gutter={[12, 12]}
                        style={{ marginLeft: 8, marginTop: 8 }}
                      >
                        {[
                          // {
                          //   label: "ยอดปิดสัญญา",
                          //   // 2. ใช้ matchedCal.closingbalance แทน cal.closingbalance
                          //   value: formatMoney(matchedCal.closingbalance),
                          //   color: THEME.success,
                          //   icon: "💰",
                          // },
                          {
                            label: "ยอดชดใช้แทน",
                            value: formatMoney(defendant?.judgement),
                            color: THEME.danger,
                            icon: "💰",
                          },
                          {
                            label: "ค่าขาดประโยชน์",
                            value: formatMoney(defendant?.lackOfBenefits),
                            color: THEME.primary,
                            icon: "📉",
                          },
                          {
                            label: "ชำระแล้ว",
                            value: formatMoney(defendant?.PAY),
                            color: THEME.success,
                            icon: "✅",
                          },
                          {
                            label: "ค่าขาดประโยชน์รายเดือน",
                            value: formatMoney(
                              defendant?.costOfUselesenessPerMonth,
                            ),
                            color: THEME.warning,
                            icon: "📆",
                          },
                          {
                            label: "ค่าติดตาม",
                            value: formatMoney(defendant?.trackingFee),
                            color: THEME.primary,
                            icon: "🔍",
                          },
                          {
                            label: "ระยะเวลาค่าขาดประโยชน์",
                            value: defendant?.costOfUselesenessMonth,
                            color: THEME.warning,
                            icon: "📆",
                          },
                          {
                            label: "ค่าทนาย",
                            value: formatMoney(defendant?.attorneyFees),
                            color: THEME.primary,
                            icon: "⚖️",
                          },
                          {
                            label: "ค่าธรรมเนียม",
                            value: formatMoney(defendant?.fee),
                            color: THEME.primary,
                            icon: "🧾",
                          },
                        ].map(({ label, value, color, icon }) => (
                          <Col span={12} key={label}>
                            <div
                              style={{
                                background: "#fff",
                                border: "1px solid #f0f0f0",
                                borderRadius: 8,
                                padding: "8px 12px",
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                              }}
                            >
                              <span style={{ fontSize: 18 }}>{icon}</span>
                              <div>
                                <Text
                                  type="secondary"
                                  style={{ fontSize: 16, display: "block" }}
                                >
                                  {label}
                                </Text>
                                <Text strong style={{ color, fontSize: 18 }}>
                                  {value}{" "}
                                  <span
                                    style={{
                                      color: "#aaa",
                                      fontWeight: 400,
                                      fontSize: 12,
                                    }}
                                  >
                                    {value === defendant?.costOfUselesenessMonth
                                      ? "เดือน"
                                      : "บาท"}
                                  </span>
                                </Text>
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                      <Divider style={{ margin: "12px 0" }} />
                      <Table
                        dataSource={judgeData?.chqtran?.filter(
                          (item) =>
                            item?.CUSTOMER_ID === defendant?.CUSTOMER_ID,
                        )}
                        columns={[
                          {
                            title: "#",
                            key: "index",
                            align: "center",
                            render: (_, __, idx) => idx + 1,
                          },
                          {
                            title: "วันที่ชำระ",
                            dataIndex: "PAYDT",
                            key: "PAYDT",
                            align: "center",
                            render: (val) => formatDate(val),
                          },
                          {
                            title: "ใบเสร็จ",
                            dataIndex: "TMBILL",
                            key: "TMBILL",
                            align: "center",
                            render: (val) => (
                              <Text
                                style={{
                                  fontFamily: "monospace",
                                  fontSize: 16,
                                }}
                              >
                                {val}
                              </Text>
                            ),
                          },
                          {
                            title: "ประเภทจ่าย",
                            dataIndex: "FORDESC",
                            key: "FORDESC",
                            align: "center",
                          },
                          {
                            title: "จำนวนเงิน",
                            dataIndex: "NETPAY",
                            key: "NETPAY",
                            width: 40,
                            align: "right",
                            render: (val, record) => (
                              <Text
                                style={{
                                  color: record.FLAG === "C" ? "red" : "green",
                                }}
                              >
                                {formatMoney(val)}
                              </Text>
                            ),
                          },
                          {
                            title: "สถานะ",
                            dataIndex: "FLAG",
                            key: "FLAG",
                            width: 40,
                            align: "right",
                            render: (val) => (
                              <Tag color={val === "C" ? "red" : "green"}>
                                {val === "C" ? "ยกเลิก" : "รับปกติ"}
                              </Tag>
                            ),
                          },
                          {
                            title: "ผู้รับ",
                            dataIndex: "INPUT_BY",
                            key: "INPUT_BY",
                            align: "center",
                            render: (val) => <Tag color="blue">{val}</Tag>,
                          },
                        ]}
                        rowKey="wctId"
                        pagination={{
                          defaultPageSize: 5,
                          showSizeChanger: true,
                        }}
                        size="small"
                        scroll={{ x: 500 }}
                        summary={() => {
                          // 3. กรอง CUSTOMER_ID ให้ตรงกับจำเลยคนนี้ เพื่อให้ยอดรวมถูกต้อง
                          const total = judgeData?.chqtran
                            ?.filter(
                              (r) =>
                                r.FLAG !== "C" &&
                                r.CUSTOMER_ID === defendant?.CUSTOMER_ID,
                            )
                            .reduce((sum, r) => sum + Number(r.NETPAY || 0), 0);

                          return (
                            <Table.Summary.Row
                              style={{ background: THEME.light }}
                            >
                              <Table.Summary.Cell colSpan={4}>
                                <Text strong>รวมชำระ</Text>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell align="right">
                                <Text
                                  strong
                                  style={{ color: THEME.success, fontSize: 18 }}
                                >
                                  {formatMoney(total)}
                                </Text>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell />
                            </Table.Summary.Row>
                          );
                        }}
                      />
                    </div>
                  ),
                };
              })}
            />
          </StyledCard>
        </div>
      );
    };

    const judgeTabs = judgeNumbers
      .filter((judgeNum) => judgeNum !== "0") // ✅ กรอง key "0" ออก
      .map((judgeNum) => ({
        key: judgeNum,
        label: (
          <span>
            <SafetyCertificateOutlined />
            คำพิพากษาที่ {judgeNum}
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {groupedJudges[judgeNum]?.length} คน
            </Tag>
          </span>
        ),
        children: renderJudgeContent(judgeNum),
      }));

    if (!judgeTabs?.length) return null;

    return (
      <Tabs
        type="card"
        size="middle"
        items={judgeTabs}
        style={{ marginBottom: 16 }}
      />
    );
  };
  // ==================== TAB 11: ค่าใช้จ่าย ====================
  const renderPaymentInCase = () => {
    console.log("judgeData?.work_expense_list?");

    // if (
    //   !judgeData?.work_expense_list?.length ||
    //   !judgeData?.work_expense_list ||
    //   !judgeData?.judge ||
    //   !judgeData?.judge?.length
    // )
    //   return <Empty description="ไม่มีข้อมูล" />;
    return (
      <>
        {judgeData?.work_expense_list?.length > 0 && (
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <StyledCard
                title="ตารางค่าใช้จ่ายในคดี"
                icon={
                  <DollarOutlined
                    style={{ color: THEME.warning, fontSize: 18 }}
                  />
                }
                style={{ height: "100%" }}
              >
                <Table
                  dataSource={judgeData.work_expense_list}
                  columns={[
                    {
                      title: "#",
                      key: "index",
                      width: 10,
                      align: "center",
                      render: (_, __, idx) => idx + 1,
                    },
                    {
                      title: "เลขที่ใบเสร็จ",
                      dataIndex: "TMBILL",
                      key: "TMBILL",
                      width: 60,
                      render: (val) => (
                        <Text style={{ fontFamily: "monospace", fontSize: 14 }}>
                          {val || "-"}
                        </Text>
                      ),
                    },
                    {
                      title: "รายการ",
                      dataIndex: "description",
                      key: "description",
                      width: 80,
                      render: (val) => <Text>{val || "-"}</Text>,
                    },
                    {
                      title: "จำนวนเงินเบิก",
                      dataIndex: "NETPAY",
                      key: "NETPAY",
                      width: 40,
                      align: "right",
                      render: (val) => (
                        <Text strong style={{ color: THEME.success }}>
                          {formatMoney(val)}
                        </Text>
                      ),
                    },
                    {
                      title: "จำนวนเงินจ่ายจริง",
                      dataIndex: "PAY",
                      key: "PAY",
                      width: 40,
                      align: "right",
                      render: (val) => (
                        <Text
                          style={{ color: val ? THEME.success : THEME.danger }}
                        >
                          {val ? formatMoney(val) : "-"}
                        </Text>
                      ),
                    },
                    {
                      title: "วันที่ชำระ",
                      dataIndex: "PAYDT",
                      key: "PAYDT",
                      width: 50,
                      align: "center",
                      render: (val) => (
                        <Text>
                          {val ? (
                            formatDate(val)
                          ) : (
                            <Tag color="orange">ยังไม่ชำระ</Tag>
                          )}
                        </Text>
                      ),
                    },
                    {
                      title: "สถานะ",
                      dataIndex: "FLAG",
                      key: "FLAG",
                      width: 40,
                      align: "center",
                      render: (val, record) => {
                        if (val === "C") return <Tag color="red">ยกเลิก</Tag>;
                        if (record.PAYDT)
                          return <Tag color="green">เคลียร์แล้ว</Tag>;
                        return <Tag color="orange">รอเคลียร์</Tag>;
                      },
                    },
                  ]}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  summary={() => {
                    const totalNetpay = judgeData.work_expense_list
                      .filter((r) => r.FLAG !== "C")
                      .reduce((sum, r) => sum + Number(r.NETPAY || 0), 0);

                    const totalPaid = judgeData.work_expense_list
                      .filter((r) => r.FLAG !== "C")
                      .reduce((sum, r) => sum + Number(r.PAY || 0), 0);

                    return (
                      <Table.Summary.Row style={{ background: THEME.light }}>
                        <Table.Summary.Cell colSpan={3}>
                          <Text strong>รวมทั้งหมด</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell align="right">
                          <Text strong style={{ color: THEME.success }}>
                            {formatMoney(totalNetpay)}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell align="right">
                          <Text strong style={{ color: THEME.success }}>
                            {formatMoney(totalPaid)}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell colSpan={2} />
                      </Table.Summary.Row>
                    );
                  }}
                />
              </StyledCard>
            </Col>
          </Row>
        )}
        {judgeData?.judge?.length > 0 && (
          <>
            <Divider style={{ margin: "12px 0" }}>ยอดศาลสั่งจ่าย</Divider>
            <Row gutter={[12, 12]} style={{ marginLeft: 8, marginTop: 8 }}>
              {[
                {
                  label: "ยอดชดใช้แทน",
                  value: formatMoney(judgeData?.judge?.[0]?.judgement),
                  color: THEME.danger,
                  icon: "💰",
                },
                {
                  label: "ค่าขาดประโยชน์",
                  value: formatMoney(judgeData?.judge?.[0]?.lackOfBenefits),
                  color: THEME.primary,
                  icon: "📉",
                },

                {
                  label: "ค่าขาดประโยชน์รายเดือน",
                  value: formatMoney(
                    judgeData?.judge?.[0]?.costOfUselesenessPerMonth,
                  ),
                  color: THEME.warning,
                  icon: "📆",
                },
                {
                  label: "ค่าติดตาม",
                  value: formatMoney(judgeData?.judge?.[0]?.trackingFee),
                  color: THEME.primary,
                  icon: "🔍",
                },
                {
                  label: "ระยะเวลาค่าขาดประโยชน์",
                  value: judgeData?.judge?.[0]?.costOfUselesenessMonth,
                  color: THEME.warning,
                  icon: "📆",
                },
                {
                  label: "ค่าทนาย",
                  value: formatMoney(judgeData?.judge?.[0]?.attorneyFees),
                  color: THEME.primary,
                  icon: "⚖️",
                },
                {
                  label: "ค่าธรรมเนียม",
                  value: formatMoney(judgeData?.judge?.[0]?.fee),
                  color: THEME.primary,
                  icon: "🧾",
                },
                {
                  label: "รวมที่ต้องชำระ",
                  value: formatMoney(judgeData?.judge?.[0]?.totalJugde),
                  color: THEME.danger,
                  icon: "💳",
                },
              ].map(({ label, value, color, icon }) => (
                <Col span={12} key={label}>
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #f0f0f0",
                      borderRadius: 8,
                      padding: "8px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <span style={{ fontSize: 18 }}>{icon}</span>
                    <div>
                      <Text
                        type="secondary"
                        style={{ fontSize: 16, display: "block" }}
                      >
                        {label}
                      </Text>
                      <Text strong style={{ color, fontSize: 18 }}>
                        {value}{" "}
                        <span
                          style={{
                            color: "#aaa",
                            fontWeight: 400,
                            fontSize: 12,
                          }}
                        >
                          {value ===
                          judgeData?.judge?.[0]?.costOfUselesenessMonth
                            ? "เดือน"
                            : "บาท"}
                        </span>
                      </Text>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
            <Divider style={{ margin: "12px 0" }} />
          </>
        )}
      </>
    );
  };

  // ==================== TAB 2: ข้อมูลสัญญาใหม่ ====================
  const renderLoanNewInfo = () => {
    const loan = judgeData?.loanNew;
    if (!loan) return <Empty description="ไม่มีข้อมูล" />;
    return (
      <StyledCard
        title="ข้อมูลสัญญา(ใหม่)"
        icon={<BankOutlined style={{ color: THEME.primary, fontSize: 18 }} />}
        style={{ marginBottom: 16 }}
        extra={
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrintCardDebtor}
            size="small"
          >
            พิมพ์
          </Button>
        }
      >
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8} md={6}>
            <StatBox
              title="ยอดจัดทั้งหมด"
              value={loan?.LOAN_NEW_TONBALANCE}
              color={THEME.primary}
              icon={<DollarOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <StatBox
              title="ชำระแล้ว"
              value={loan?.LOAN_NEW_SMPAY}
              color={THEME.success}
              icon={<CheckCircleOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <StatBox
              title="ค่างวด"
              value={loan?.TOT_UPAY}
              color={THEME.info}
              icon={<WarningOutlined />}
            />
          </Col>
        </Row>
        <Divider style={{ margin: "20px 0" }} />
        <Row gutter={[32, 16]}>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="สัญญาเก่าคือ" value={`${loan?.ISSUNO || "-"}`} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="สัญญาใหม่"
              value={`${judgeData?.loanNew?.NEWCONTNO || "-"}`}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="วันทำสัญญา"
              value={formatDate(loan?.SDATE)}
              icon={<CalendarOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="งวดแรก" value={formatDate(loan?.FDATE)} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="งวดสุดท้าย" value={formatDate(loan?.LDATE)} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="จำนวนงวด" value={`${loan?.T_NOPAY || 0} งวด`} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="งวดละ"
              value={`${formatMoney(loan?.TOT_UPAY)} บาท`}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="ชำระล่าสุด" value={formatDate(loan?.LPAYD)} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="ยอดชำระล่าสุด"
              value={`${formatMoney(loan?.LPAYA)} บาท`}
            />
          </Col>

          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="ค้างจาก"
              value={`${judgeData?.loanNew?.EXP_FRM || "-"}`}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="ค้างถึง"
              value={`${judgeData?.loanNew?.EXP_TO || "-"}`}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="ค้างประมาณ"
              value={`${judgeData?.loanNew?.EXP_PRD || "-"}`}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="ค้างจริง"
              value={`${judgeData?.loanNew?.HLDNO || "-"}`}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="ยอดค้าง"
              value={`${formatMoney(judgeData?.loanNew?.EXP_AMT) || "-"}`}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="เกรด"
              value={`${judgeData?.loanNew?.GRDCOD || "-"}`}
            />
          </Col>
        </Row>
      </StyledCard>
    );
  };

  // สร้างคอลัมน์ (ต่างกันแค่ type ของปุ่มพิมพ์)
  const buildPaymentColumns = (printType) => [
    {
      title: "ลำดับ",
      key: "index",
      width: 60,
      align: "center",
      render: (_, __, idx) => idx + 1,
    },
    {
      title: "เลขที่บิล",
      dataIndex: "TMBILL",
      key: "TMBILL",
      render: (val) => <Text style={{ fontFamily: "monospace" }}>{val}</Text>,
    },
    {
      title: "วันที่บิล",
      dataIndex: "TMBILDT",
      key: "TMBILDT",
      render: (val) => formatDate(val),
    },
    {
      title: "ชำระโดย",
      dataIndex: "PAYTYP",
      key: "PAYTYP",
    },
    {
      title: "รหัสชำระ",
      dataIndex: "PAYFOR",
      key: "PAYFOR",
    },
    {
      title: "รายการ",
      dataIndex: "FORDESC",
      key: "FORDESC",
    },
    {
      title: "ชำระงวด",
      dataIndex: "PAYAMT",
      key: "PAYAMT",
      align: "right",
      render: (val) => (
        <Text strong style={{ color: THEME.success }}>
          {formatMoney(val)} บาท
        </Text>
      ),
    },
    {
      title: "ชำระดอก",
      dataIndex: "PAYAMT_V",
      key: "PAYAMT_V",
      align: "right",
      render: (val) => (
        <Text strong style={{ color: THEME.success }}>
          {formatMoney(val)} บาท
        </Text>
      ),
    },
    {
      title: "ชำระต้น",
      dataIndex: "PAYAMT_N",
      key: "PAYAMT_N",
      align: "right",
      render: (val) => (
        <Text strong style={{ color: THEME.success }}>
          {formatMoney(val)} บาท
        </Text>
      ),
    },
    {
      title: "- ส่วนลด",
      dataIndex: "DISCT",
      key: "DISCT",
      align: "right",
      render: (val) => (
        <Text strong style={{ color: THEME.danger }}>
          {formatMoney(val)} บาท
        </Text>
      ),
    },
    {
      title: "วันที่ชำระ",
      dataIndex: "PAYDT",
      key: "PAYDT",
      render: (val) => formatDate(val),
    },
    {
      title: "รับสุทธิ",
      dataIndex: "NETPAY",
      key: "NETPAY",
      align: "right",
      render: (val) => (
        <Text strong style={{ color: THEME.success }}>
          {formatMoney(val)} บาท
        </Text>
      ),
    },
    {
      title: "สถานะ",
      dataIndex: "FLAG",
      key: "FLAG",
      width: 40,
      align: "right",
      render: (val) => (
        <Tag color={val === "C" ? "red" : "green"}>
          {val === "C" ? "ยกเลิก" : "รับปกติ"}
        </Tag>
      ),
    },
    {
      title: "ผู้รับ",
      dataIndex: "INPUT_BY",
      key: "INPUT_BY",
      align: "center",
      render: (val) => <Tag color="blue">{val}</Tag>,
    },
    {
      title: "การจัดการ",
      key: "actions",
      align: "center",
      width: 140,
      render: (_, record) => (
        <Button
          icon={<PrinterOutlined />}
          // disabled={record.FLAG === "C"} // บิลยกเลิกไม่ให้พิมพ์
          onClick={() => handlePrintReceipt(record, printType)}
        >
          พิมพ์ใบเสร็จ
        </Button>
      ),
    },
  ];

  // สร้าง summary รวมทุกคอลัมน์เงิน (ไม่รวมรายการที่ยกเลิก)
  const buildPaymentSummary = (data) => () => {
    const rows = (data || []).filter((r) => r.FLAG !== "C");
    const sumBy = (field) =>
      rows.reduce((sum, r) => sum + Number(r[field] || 0), 0);

    const MoneyCell = ({ value, index, color = THEME.success }) => (
      <Table.Summary.Cell index={index} align="right">
        <Text strong style={{ color }}>
          {formatMoney(value)} บาท
        </Text>
      </Table.Summary.Cell>
    );

    return (
      <Table.Summary.Row style={{ background: THEME.light }}>
        {/* ลำดับ, เลขที่บิล, วันที่บิล, ชำระโดย, รหัสชำระ, รายการ */}
        <Table.Summary.Cell index={0} colSpan={6}>
          <Text strong>รวมทั้งหมด (ไม่รวมยกเลิก)</Text>
        </Table.Summary.Cell>
        <MoneyCell index={6} value={sumBy("PAYAMT")} /> {/* ชำระงวด */}
        <MoneyCell index={7} value={sumBy("PAYAMT_V")} /> {/* ชำระดอก */}
        <MoneyCell index={8} value={sumBy("PAYAMT_N")} /> {/* ชำระต้น */}
        <MoneyCell index={9} value={sumBy("DISCT")} color={THEME.danger} />{" "}
        {/* ส่วนลด */}
        <Table.Summary.Cell index={10} /> {/* วันที่ชำระ */}
        <MoneyCell index={11} value={sumBy("NETPAY")} /> {/* รับสุทธิ */}
        {/* สถานะ, ผู้รับ, การจัดการ */}
        <Table.Summary.Cell index={12} colSpan={3} />
      </Table.Summary.Row>
    );
  };

  // ค่า pagination ใช้ร่วมกัน
  const paymentPagination = {
    defaultPageSize: 5,
    showSizeChanger: true,
    pageSizeOptions: ["5", "10", "20", "50", "100"],
  };

  // ---------- ฟังก์ชันพิมพ์ใบเสร็จ (โหลดข้อมูลจาก API แล้วสร้าง PDF ด้วย jsPDF) ----------
  const handlePrintReceipt = async (record, type) => {
    // type: "normal" = chqtran → PaymentLawyerPdfJs
    // type: "ar"     = chqtran_arothr → PaymentArLawyerPdfjs
    if (!record?.TMBILL) return message.warning("ไม่พบใบเสร็จ");

    const isAr = type === "ar";
    const msgKey = isAr ? "load_process_ar_print" : "load_process_print";

    // หน้าเดิมใช้ record.REFDOCNO แต่ข้อมูลใน judgeData ใช้ CONTNO
    const contno = record?.CONTNO || record?.REFDOCNO;

    const url = isAr
      ? `${BASE_URL_BEN}/auth/print-payment-lawyer-ar`
      : `${BASE_URL_BEN}/auth/print-payment-lawyer`;

    const finalPayload = isAr
      ? { TMBILL: record?.TMBILL, CONTNO: contno }
      : { TMBILL: record?.TMBILL, CONTNO: contno, PAYFOR: record?.PAYFOR };

    try {
      message.loading({
        content: "กำลังโหลดข้อมูลใบเสร็จ...",
        key: msgKey,
      });

      const response = await axios.post(url, finalPayload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 && response.data) {
        message.success({
          content: "โหลดข้อมูลสำเร็จ",
          key: msgKey,
        });

        if (isAr) await PaymentArLawyerPdfjs(response.data);
        else await PaymentLawyerPdfJs(response.data);
      } else {
        message.warning({
          content: "ไม่พบข้อมูลใบเสร็จ",
          key: msgKey,
        });
      }
    } catch (error) {
      console.log("error print", error);
      message.error({
        content: "เกิดข้อผิดพลาดในการโหลดใบเสร็จ",
        key: msgKey,
      });
    }
  };

  // ---------- render ----------
  const renderPayment = () => {
    if (!judgeData?.chqtran?.length && !judgeData?.chqtran_arothr?.length)
      return <Empty description="ไม่มีข้อมูล" />;
    return (
      <>
        {judgeData?.chqtran?.length > 0 && (
          <StyledCard
            title={
              judgeData?.loanNew
                ? "ตารางการรับชำระรวม(ทำยอม)"
                : "ตารางการรับชำระปกติ"
            }
            icon={
              <DollarOutlined
                style={{
                  color: judgeData?.loanNew ? THEME.primary : THEME.danger,
                  fontSize: 18,
                }}
              />
            }
            style={{ marginBottom: 16 }}
          >
            <Table
              dataSource={judgeData.chqtran}
              columns={buildPaymentColumns("normal")}
              rowKey="wctId"
              pagination={paymentPagination}
              size="middle"
              scroll={{ x: 1500 }}
              summary={buildPaymentSummary(judgeData?.chqtran)}
            />
          </StyledCard>
        )}

        {judgeData?.chqtran_arothr?.length > 0 && (
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <StyledCard
                title="ตารางการรับลูกหนี้อื่น ๆ"
                icon={
                  <DollarOutlined
                    style={{ color: THEME.danger, fontSize: 18 }}
                  />
                }
                style={{ height: "100%" }}
              >
                <Table
                  dataSource={judgeData.chqtran_arothr}
                  columns={buildPaymentColumns("ar")}
                  rowKey="wctId"
                  pagination={paymentPagination}
                  size="middle"
                  scroll={{ x: 1500 }}
                  summary={buildPaymentSummary(judgeData?.chqtran_arothr)}
                />
              </StyledCard>
            </Col>
          </Row>
        )}
      </>
    );
  };

  const handleDueDate = (val, record) => {
    const today = dayjs();
    const dueDate = dayjs(record.DUEDATE);
    const hasPaydate = record.PAYDATE && dayjs(record.PAYDATE).isValid();
    const useDate = hasPaydate ? dayjs(record.PAYDATE) : today;

    const isOverdue = dueDate.isBefore(useDate, "day");
    const diffDays = useDate.diff(dueDate, "day");

    return (
      <Tag
        color={isOverdue ? "red" : hasPaydate ? "green" : "default"}
        icon={
          isOverdue ? (
            <WarningOutlined />
          ) : hasPaydate ? (
            <CheckCircleOutlined />
          ) : undefined
        }
      >
        {isOverdue
          ? `เลยกำหนด ${diffDays} วัน`
          : hasPaydate
            ? `ปกติ`
            : `รอชำระ`}
      </Tag>
    );
  };

  const handleDueAmt = (val, record) => {
    const today = dayjs();
    const dueDate = dayjs(record.DUEDATE);
    const dueAmt = Number(val || 0);
    const payAmt = Number(record.PAYAMT || 0);
    const isFullyPaid = payAmt > 0 && payAmt >= dueAmt;
    const isPartiallyPaid = payAmt > 0 && payAmt < dueAmt;
    const isOverdue = dueDate.isBefore(today, "day");

    const color = isFullyPaid
      ? THEME.success
      : isPartiallyPaid
        ? THEME.info
        : isOverdue
          ? THEME.danger
          : THEME.dark;

    return (
      <Text
        style={{
          fontFamily: "monospace",
          fontSize: 14,
          color,
        }}
      >
        {formatMoney(val)}
      </Text>
    );
  };

  const mergedDuePaymentData = useMemo(
    () => mergeDuePaymentWithPenalty(duePaymentData, beeypupData),
    [duePaymentData, beeypupData],
  );

  const renderPenaltyLabel = (record) => (
    <Space size={6}>
      <WarningOutlined style={{ color: THEME.warning }} />
      <Text strong style={{ color: THEME.warning }}>
        เบี้ยปรับ งวดที่ {record.NOPAY}
      </Text>
    </Space>
  );

  const handlePrintDuePayment = async () => {
    if (!mergedDuePaymentData?.length) {
      return message.warning("ไม่มีข้อมูลตารางดิว");
    }
    try {
      let customer = loanData?.CUSTOMER;
      if (!customer) {
        const contnoForLoan =
          judgeData?.judge?.[0]?.CONTNO || searchData?.CONTNO;
        const fresh = await loadLoanData(contnoForLoan);
        customer = fresh?.CUSTOMER;
      }
      const customerName = customer
        ? `${customer?.SNAM || ""}${customer?.NAME1 || ""} ${customer?.NAME2 || ""}`.trim()
        : "";

      await exportDuePaymentPdf({
        contno:
          duePaymentData?.[0]?.CONTNO ||
          judgeData?.loanNew?.NEWCONTNO ||
          judgeData?.judge?.[0]?.CONTNO ||
          "",
        customerName,
        rows: mergedDuePaymentData,
      });
    } catch (error) {
      console.error("handlePrintDuePayment error:", error);
      message.error("เกิดข้อผิดพลาดในการสร้างเอกสาร");
    }
  };

  const handlePrintCardDebtor = async () => {
    const isJudgeRecord = !judgeData?.loanNew;
    const loanNew = judgeData?.loanNew
      ? judgeData?.loanNew
      : judgeData?.judge?.[0];
    if (!loanNew) {
      return message.warning("ไม่มีข้อมูลสัญญา(ใหม่)");
    }
    try {
      let freshLoanData = loanData;
      if (!freshLoanData) {
        const contnoForLoan =
          loanNew?.ISSUNO ||
          judgeData?.judge?.[0]?.CONTNO ||
          searchData?.CONTNO;
        freshLoanData = await loadLoanData(contnoForLoan);
      }

      await exportCardDebtorPdf({
        loanNew,
        customer: freshLoanData?.CUSTOMER,
        guarantors: freshLoanData?.GUARANTORS,
        mortgage: freshLoanData?.MORTGAGE,
        isJudgeRecord,
        chqtran: judgeData?.chqtran,
        chqtranArothr: judgeData?.chqtran_arothr,
        flagLoanNew: judgeData?.flagLoanNew,
      });
    } catch (error) {
      console.error("handlePrintCardDebtor error:", error);
      message.error("เกิดข้อผิดพลาดในการสร้างเอกสาร");
    }
  };

  const renderduePayment = () => {
    const paidCount =
      duePaymentData?.filter((r) => Number(r.PAYAMT) > 0)?.length || 0;
    const totalCount = duePaymentData?.length || 0;

    return (
      <>
        <StyledCard
          title="ตารางดิว"
          icon={
            <DollarOutlined style={{ color: THEME.danger, fontSize: 18 }} />
          }
          extra={
            <Space>
              <Tag color="blue" style={{ margin: 0 }}>
                ชำระแล้ว {paidCount}/{totalCount} งวด
              </Tag>
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                onClick={handlePrintDuePayment}
                size="small"
              >
                พิมพ์
              </Button>
            </Space>
          }
          style={{ height: "100%" }}
        >
          <Table
            dataSource={mergedDuePaymentData}
            bordered
            scroll={{ x: "max-content" }}
            columns={[
              {
                title: "งวดที่",
                dataIndex: "NOPAY",
                key: "NOPAY",
                width: 70,
                align: "center",
                onCell: (record) =>
                  record.__rowType === "penalty" ? { colSpan: 2 } : {},
                render: (val, record) => {
                  if (record.__rowType === "penalty") {
                    return renderPenaltyLabel(record);
                  }
                  const isPaid = Number(record.PAYAMT) > 0;
                  const color = isPaid ? THEME.success : THEME.primary;
                  return (
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        margin: "0 auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "50%",
                        fontWeight: 600,
                        fontSize: 13,
                        color,
                        background: `${color}15`,
                        border: `1px solid ${color}40`,
                      }}
                    >
                      {val}
                    </div>
                  );
                },
              },
              {
                title: (
                  <Space size={4}>
                    <CalendarOutlined /> วันครบกำหนด
                  </Space>
                ),
                dataIndex: "DUEDATE",
                key: "DUEDATE",
                width: 130,
                align: "center",
                onCell: (record) =>
                  record.__rowType === "penalty" ? { colSpan: 0 } : {},
                render: (val) => formatDate(val),
              },
              {
                title: (
                  <Space size={4}>
                    <CheckCircleOutlined /> วันที่ชำระ
                  </Space>
                ),
                dataIndex: "PAYDATE",
                key: "PAYDATE",
                width: 130,
                align: "center",
                render: (val, record) => {
                  if (record.__rowType === "penalty") {
                    const isPaid = Number(record.PAY_BEEYPUP || 0) > 0;
                    return isPaid ? (
                      formatDate(record.PAYDATE)
                    ) : (
                      <Text type="secondary">-</Text>
                    );
                  }
                  return formatDate(val);
                },
              },
              {
                title: "สถานะ",
                dataIndex: "DELAYDAY",
                key: "DELAYDAY",
                width: 140,
                align: "center",
                render: (val, record) => {
                  if (record.__rowType === "penalty") {
                    const delayDay = Number(record.DELAYDAY || 0);
                    return <Tag color="orange">ค้าง {delayDay} วัน</Tag>;
                  }
                  return handleDueDate(val, record);
                },
              },
              {
                title: "ลูกหนี้ทั้งหมด",
                dataIndex: "TONBALANCE",
                key: "TONBALANCE",
                width: 120,
                align: "right",
                render: (val, record) => {
                  if (record.__rowType === "penalty") {
                    return <Text type="secondary">-</Text>;
                  }
                  return (
                    <Text style={{ fontFamily: "monospace", fontSize: 14 }}>
                      {formatMoney(val)}
                    </Text>
                  );
                },
              },
              {
                title: "ค่างวด",
                dataIndex: "DUEAMT",
                key: "DUEAMT",
                width: 120,
                align: "right",
                render: (val, record) => {
                  if (record.__rowType === "penalty") {
                    const beeypup = Number(record.BEEYPUP || 0);
                    const payBeeypup = Number(record.PAY_BEEYPUP || 0);
                    const isFullyPaid = payBeeypup > 0 && payBeeypup >= beeypup;
                    const isPartiallyPaid =
                      payBeeypup > 0 && payBeeypup < beeypup;
                    const penaltyColor = isFullyPaid
                      ? THEME.success
                      : isPartiallyPaid
                        ? THEME.info
                        : THEME.danger;
                    return (
                      <Text
                        strong
                        style={{
                          fontFamily: "monospace",
                          fontSize: 14,
                          color: penaltyColor,
                        }}
                      >
                        {formatMoney(record.BEEYPUP)}
                      </Text>
                    );
                  }
                  return handleDueAmt(val, record);
                },
              },
              {
                title: "ชำระแล้ว",
                dataIndex: "PAYAMT",
                key: "PAYAMT",
                width: 120,
                align: "right",
                render: (val, record) => {
                  if (record.__rowType === "penalty") {
                    const payBeeypup = Number(record.PAY_BEEYPUP || 0);
                    return (
                      <Text
                        strong
                        style={{
                          fontFamily: "monospace",
                          fontSize: 14,
                          color: payBeeypup > 0 ? THEME.success : THEME.danger,
                        }}
                      >
                        {formatMoney(payBeeypup)}
                      </Text>
                    );
                  }
                  return (
                    <Text
                      style={{
                        fontFamily: "monospace",
                        fontSize: 14,
                        color:
                          Number(record.PAYAMT) > 0
                            ? THEME.success
                            : THEME.danger,
                      }}
                    >
                      {formatMoney(val)}
                    </Text>
                  );
                },
              },
            ]}
            rowKey="_key"
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
            }}
            size="small"
            onRow={(record) =>
              record.__rowType === "penalty"
                ? { style: { background: `${THEME.warning}12` } }
                : {
                    style: {
                      background:
                        Number(record.PAYAMT) > 0
                          ? `${THEME.success}08`
                          : undefined,
                    },
                  }
            }
            summary={() => {
              const totalDue = duePaymentData?.reduce(
                (sum, r) => sum + Number(r.DUEAMT || 0),
                0,
              );
              const totalPaid = duePaymentData?.reduce(
                (sum, r) => sum + Number(r.PAYAMT || 0),
                0,
              );
              const remaining = totalDue - totalPaid;
              const totalPenaltyCharged = beeypupData?.reduce(
                (sum, r) => sum + Number(r.BEEYPUP || 0),
                0,
              );
              const totalPenaltyPaid = beeypupData?.reduce(
                (sum, r) => sum + Number(r.PAY_BEEYPUP || 0),
                0,
              );
              const remainingPenalty = totalPenaltyCharged - totalPenaltyPaid;

              return (
                <>
                  <Table.Summary.Row style={{ background: THEME.light }}>
                    <Table.Summary.Cell colSpan={4}>
                      <Text strong>รวมทั้งหมด</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <Text
                        strong
                        style={{
                          fontFamily: "monospace",
                          fontSize: 14,
                          color: remaining > 0 ? THEME.danger : THEME.success,
                        }}
                      >
                        {formatMoney(remaining)}
                      </Text>
                      <div>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          คงเหลือ
                        </Text>
                      </div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <Text
                        strong
                        style={{ fontFamily: "monospace", fontSize: 14 }}
                      >
                        {formatMoney(totalDue)}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <Text
                        strong
                        style={{
                          color: totalPaid > 0 ? THEME.success : THEME.danger,
                          fontSize: 14,
                          fontFamily: "monospace",
                        }}
                      >
                        {formatMoney(totalPaid)}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                  <Table.Summary.Row
                    style={{ background: `${THEME.warning}10` }}
                  >
                    <Table.Summary.Cell colSpan={4}>
                      <Space size={6}>
                        <WarningOutlined style={{ color: THEME.warning }} />
                        <Text strong style={{ color: THEME.warning }}>
                          รวมเบี้ยปรับ
                        </Text>
                      </Space>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <Text
                        strong
                        style={{
                          fontFamily: "monospace",
                          fontSize: 14,
                          color:
                            remainingPenalty > 0 ? THEME.danger : THEME.success,
                        }}
                      >
                        {formatMoney(remainingPenalty)}
                      </Text>
                      <div>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          คงเหลือ
                        </Text>
                      </div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <Text
                        strong
                        style={{
                          fontFamily: "monospace",
                          fontSize: 14,
                          color: THEME.warning,
                        }}
                      >
                        {formatMoney(totalPenaltyCharged)}
                      </Text>
                      <div>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          ถูกคิด
                        </Text>
                      </div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <Text
                        strong
                        style={{
                          fontFamily: "monospace",
                          fontSize: 14,
                          color:
                            totalPenaltyPaid > 0 ? THEME.success : THEME.danger,
                        }}
                      >
                        {formatMoney(totalPenaltyPaid)}
                      </Text>
                      <div>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          ชำระแล้ว
                        </Text>
                      </div>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </>
              );
            }}
          />
        </StyledCard>
      </>
    );
  };

  const renderLoanNewInfoJudge = () => {
    const loan = judgeData?.judge?.[0];
    if (!loan) return <Empty description="ไม่มีข้อมูล" />;
    return (
      <StyledCard
        title="ข้อมูลสัญญา(พิพากษา)"
        icon={<BankOutlined style={{ color: THEME.primary, fontSize: 18 }} />}
        style={{ marginBottom: 16 }}
        extra={
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrintCardDebtor}
            size="small"
          >
            พิมพ์
          </Button>
        }
      >
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8} md={6}>
            <StatBox
              title="ยอดจัดทั้งหมด"
              value={loan?.totalJugde}
              color={THEME.primary}
              icon={<DollarOutlined />}
            />
          </Col>
          {/* <Col xs={12} sm={8} md={6}>
            <StatBox
              title="ชำระแล้ว"
              value={loan?.LOAN_NEW_SMPAY}
              color={THEME.success}
              icon={<CheckCircleOutlined />}
            />
          </Col> */}
          <Col xs={12} sm={8} md={6}>
            <StatBox
              title="ค่างวด"
              value={loan?.totalJugde}
              color={THEME.info}
              icon={<WarningOutlined />}
            />
          </Col>
        </Row>
        <Divider style={{ margin: "20px 0" }} />
        <Row gutter={[32, 16]}>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="วันทำสัญญา"
              value={formatDate(loan?.SDATE)}
              icon={<CalendarOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="งวดแรก" value={formatDate(loan?.FDATE)} />
          </Col>

          <Col xs={12} sm={8} md={4}>
            <InfoItem label="จำนวนงวด" value={`1 งวด`} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="งวดละ"
              value={`${formatMoney(loan?.totalJugde)} บาท`}
            />
          </Col>

          <Col xs={12} sm={8} md={4}>
            <InfoItem label="สัญญาเก่าคือ" value={`${loan?.CONTNO || "-"}`} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="สัญญาใหม่" value={`${loan?.newContno || "-"}`} />
          </Col>
        </Row>
      </StyledCard>
    );
  };

  const renderInvestigationInfo = () => {
    const asset = judgeData?.asset ?? [];

    // garno=0 และ relationCustomerId=null → ผู้กู้หลัก
    const mainBorrower = asset.filter(
      (r) => r.garno === 0 && r.relationCustomerId === null,
    );

    // garno=0 แต่มี relationCustomerId → คู่สมรสของผู้ค้ำ
    const spouseMap = {};
    asset
      .filter((r) => r.garno === 0 && r.relationCustomerId !== null)
      .forEach((r) => {
        spouseMap[r.relationCustomerId] = r;
      });

    // guarantors เรียงตาม garno แล้ว insert คู่สมรสหลังแต่ละคน
    const guarantorsSorted = asset
      .filter((r) => r.garno > 0)
      .sort((a, b) => a.garno - b.garno);

    const guarantors = [];
    guarantorsSorted.forEach((g) => {
      guarantors.push(g);
      const spouse = spouseMap[g.customerId];
      if (spouse) guarantors.push(spouse);
    });

    const PersonCard = ({ record }) => {
      const hasAsset = record.assetLand?.length > 0;
      const isSpouseOfGuarantor =
        record.garno === 0 && record.relationCustomerId !== null;

      return (
        <StyledCard
          title={
            <Space>
              <UserOutlined
                style={{
                  color:
                    record.garno === 0 && !isSpouseOfGuarantor
                      ? THEME.primary
                      : THEME.warning,
                }}
              />
              <Text strong style={{ fontSize: 14 }}>
                {record.garno === 0 && !isSpouseOfGuarantor
                  ? `ผู้กู้หลัก`
                  : record.garno === 0 && isSpouseOfGuarantor
                    ? `คู่สมรสผู้ค้ำ`
                    : `ผู้ค้ำประกัน ${record.garno}`}
              </Text>
            </Space>
          }
          style={{ marginBottom: 12 }}
        >
          <div
            style={{
              background: hasAsset
                ? "linear-gradient(135deg, #f0f7ff 0%, #f8fafc 100%)"
                : "linear-gradient(135deg, #fff0f0 0%, #fff8f8 100%)",
              borderRadius: 10,
              padding: "12px 16px",
              marginBottom: 14,
              border: hasAsset ? "1px solid #dbeafe" : "1px solid #fecaca",
            }}
          >
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={12}>
                <Space align="start">
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: hasAsset ? THEME.primary : "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <UserOutlined style={{ color: "#fff", fontSize: 16 }} />
                  </div>
                  <div>
                    <Text
                      type="secondary"
                      style={{ fontSize: 14, display: "block" }}
                    >
                      ชื่อ-นามสกุล
                    </Text>
                    <Text
                      strong
                      style={{
                        fontSize: 16,
                        color: hasAsset ? undefined : "#ef4444",
                      }}
                    >
                      {`${record.SNAM}${record.NAME1} ${record.NAME2}`}
                    </Text>
                  </div>
                </Space>
              </Col>
              <Col xs={24} sm={12}>
                <Space align="start">
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: THEME.info,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <IdcardOutlined style={{ color: "#fff", fontSize: 16 }} />
                  </div>
                  <div>
                    <Text
                      type="secondary"
                      style={{ fontSize: 14, display: "block" }}
                    >
                      เลขบัตรประชาชน
                    </Text>
                    <Text
                      strong
                      style={{ fontSize: 16, letterSpacing: "0.5px" }}
                    >
                      {record.CUSCOD ?? "-"}
                    </Text>
                  </div>
                </Space>
              </Col>
            </Row>
          </div>
          <div
            style={{
              borderRadius: 10,
              border: "1px solid #e8ecf0",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #1e3a5f 0%, #2d6a9f 100%)",
                padding: "10px 16px",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span style={{ fontSize: 16 }}>🏞️</span>
              <Text strong style={{ color: "#fff", fontSize: 14 }}>
                ข้อมูลที่ดิน
              </Text>
              <Tag
                color={hasAsset ? "blue" : "red"}
                style={{ marginLeft: "auto", borderRadius: 20 }}
              >
                {record.assetLand?.length ?? 0} แปลง
              </Tag>
            </div>
            <div style={{ padding: "12px 14px", background: "#f8fafc" }}>
              {!hasAsset ? (
                <div style={{ textAlign: "center", padding: "16px 0" }}>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    ไม่มีข้อมูลที่ดิน
                  </Text>
                </div>
              ) : (
                record.assetLand.map((land, i) => (
                  <div
                    key={land.landId}
                    style={{
                      background: "#fff",
                      borderRadius: 8,
                      padding: "10px 14px",
                      marginBottom: i < record.assetLand.length - 1 ? 10 : 0,
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: 8,
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          background: THEME.primary,
                          color: "#fff",
                          borderRadius: 20,
                          padding: "1px 10px",
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        แปลงที่ {i + 1}
                      </div>
                      <Tag
                        color={land.typeLand === 1 ? "geekblue" : "default"}
                        style={{ fontSize: 11 }}
                      >
                        {land.typeLand === 1 ? "โฉนด" : "อื่นๆ"}
                      </Tag>
                    </div>
                    <Row gutter={[8, 6]}>
                      {[
                        { label: "เลขที่", value: land.numberLand, icon: "🏛️" },
                        {
                          label: "ตำบล",
                          value: land.subDistDesc ?? land.subdistrict,
                          icon: "🗾",
                        },
                        {
                          label: "อำเภอ",
                          value: land.distDesc ?? land.district,
                          icon: "🏛️",
                        },
                        {
                          label: "จังหวัด",
                          value: land.provDesc ?? land.province,
                          icon: "🗺️",
                        },
                      ].map(({ label, value, icon }) => (
                        <Col xs={12} sm={6} key={label}>
                          <div
                            style={{
                              background: "#f8fafc",
                              borderRadius: 6,
                              padding: "5px 10px",
                              border: "1px solid #e8ecf0",
                            }}
                          >
                            <Text
                              type="secondary"
                              style={{ fontSize: 14, display: "block" }}
                            >
                              {icon} {label}
                            </Text>
                            <Text style={{ fontSize: 16, fontWeight: 500 }}>
                              {value ?? "-"}
                            </Text>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                ))
              )}
            </div>
          </div>
        </StyledCard>
      );
    };

    if (asset?.length === 0) return <Empty />;
    return (
      <>
        {mainBorrower.map((r) => (
          <PersonCard key={r.customerId} record={r} />
        ))}
        {guarantors?.length > 0 && (
          <>
            <Divider orientation="left">
              <Text strong style={{ color: THEME.warning }}>
                ผู้ค้ำประกัน ({guarantorsSorted?.length} คน)
              </Text>
            </Divider>
            {guarantors.map((r) => (
              <PersonCard key={r.customerId} record={r} />
            ))}
          </>
        )}
      </>
    );
  };

  // ==================== TAB 6: ข้อมูลสัญญา (แบบฟอร์มเดิม/Delphi) ====================
  const renderLegacyContractView = () => {
    const loan = loanData?.LOAN;
    const mortgage = loanData?.MORTGAGE;
    const customer = loanData?.CUSTOMER;
    const guarantors = loanData?.GUARANTORS || [];
    const addr = customer?.ADDRESS?.[0];

    if (!loan) return <Empty description="ไม่มีข้อมูล" />;

    const interestAmount =
      Number(loan?.TOTPRC || 0) - Number(loan?.NCSHPRC || 0);

    return (
      <StyledCard
        title="ข้อมูลสัญญา (แบบฟอร์มเดิม)"
        icon={<BankOutlined style={{ color: THEME.primary, fontSize: 18 }} />}
        style={{ marginBottom: 16 }}
      >
        <Row gutter={[16, 16]}>
          {/* ซ้าย: กริดข้อมูลสัญญา + รถ */}
          <Col xs={24} lg={16}>
            <LegacySectionBar>ข้อมูลสัญญา</LegacySectionBar>
            <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
              <LegacyField label="เลขที่สัญญา" value={loan?.CONTNO} span={5} />
              <LegacyField
                label="ชื่อลูกค้า"
                value={`${customer?.SNAM || ""}${customer?.NAME1 || ""} ${customer?.NAME2 || ""}`.trim()}
                span={9}
              />
              <LegacyField
                label="วันที่ทำสัญญา"
                value={formatDate(loan?.SDATE)}
                span={5}
              />
              <LegacyField
                label="วันชำระล่าสุด"
                value={formatDate(loan?.LPAYD)}
                span={5}
              />
              <LegacyField
                label="เบอร์มือถือ"
                value={addr?.TELP || customer?.MOBILE}
                span={19}
              />
            </Row>
            <Row gutter={[8, 8]} style={{ marginBottom: 8 }}>
              <LegacyField
                label="ราคาขาย"
                value={formatMoney(loan?.NCSHPRC)}
              />
              <LegacyField
                label="ภาษีขาย"
                value={formatMoney(loan?.VATSEL ?? 0)}
              />
              <LegacyField
                label="ราคาขายรวม"
                value={formatMoney(loan?.TOTPRC)}
              />
              <LegacyField
                label="เงินดาวน์"
                value={formatMoney(loan?.NDOWN ?? 0)}
              />
              <LegacyField
                label="ภาษีดาวน์"
                value={formatMoney(loan?.VATDOWN ?? 0)}
              />
              <LegacyField
                label="เงินดาวน์รวม"
                value={formatMoney(loan?.TDOWN ?? loan?.NDOWN ?? 0)}
              />
            </Row>
            <Row gutter={[8, 8]} style={{ marginBottom: 8 }}>
              <LegacyField
                label="เงินลงทุน"
                value={formatMoney(loan?.NCSHPRC)}
              />
              <LegacyField label="จำนวนผ่อน" value={loan?.T_NOPAY} />
              <LegacyField label="งวดแรก" value={formatDate(loan?.FDATE)} />
              <LegacyField
                label="งวดสุดท้าย"
                value={formatDate(loan?.LDATE)}
              />
              <LegacyField label="ดอกเบี้ย(%) ต่อปี" value={loan?.INTRATE} />
              <LegacyField
                label="เบี้ยปรับ(%) ต่อเดือน"
                value={loan?.PENRATE}
              />
            </Row>
            <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
              <LegacyField
                label="ค่างวด"
                value={formatMoney(loan?.TOT_UPAY)}
              />
              <LegacyField label="ภาษี" value={formatMoney(loan?.VATUPAY ?? 0)} />
              <LegacyField
                label="รวมภาษี"
                value={formatMoney(loan?.TOT_UPAY)}
              />
              <LegacyField
                label="ดอกเบี้ย"
                value={formatMoney(interestAmount)}
              />
              <LegacyField
                label="ชำระเงินแล้ว"
                value={formatMoney(loan?.SMPAY)}
              />
              <LegacyField
                label="ลูกหนี้คงเหลือ"
                value={formatMoney(loan?.EXP_AMT)}
                valueColor={THEME.danger}
              />
            </Row>
            <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
              <LegacyField label="ผู้ตรวจสอบ" value={loan?.AUDITBY} />
              <LegacyField label="พนักงานเก็บเงิน" value={loan?.COLBY} />
              <LegacyField label="พนักงานขาย" value={loan?.SALEBY} />
              <LegacyField
                label="วันที่เปลี่ยนสัญญา"
                value={formatDate(loan?.CHGDATE)}
              />
              <LegacyField
                label="วันที่หยุด Vat"
                value={formatDate(loan?.VATSTOPDT)}
              />
              <LegacyField
                label="เริ่มผ่อนชำระจากงวด-ถึงงวด"
                value={
                  loan?.FRMPAY && loan?.TOPAY
                    ? `${loan?.FRMPAY} - ${loan?.TOPAY}`
                    : "-"
                }
              />
            </Row>

            <LegacySectionBar>ข้อมูลรถ / หลักประกัน</LegacySectionBar>
            <Row gutter={[8, 8]}>
              <LegacyField label="ยี่ห้อรถ" value={mortgage?.TYPE} />
              <LegacyField label="ทะเบียน" value={mortgage?.REGNO} />
              <LegacyField label="กลุ่มสินค้า" value={mortgage?.PRDGRP} />
              <LegacyField label="รุ่นรถ" value={mortgage?.MODEL} />
              <LegacyField label="เลขตัวถัง" value={mortgage?.STRNO} />
              <LegacyField label="ขนาด" value={mortgage?.SIZE} />
              <LegacyField label="สีรถ" value={mortgage?.COLOR} />
              <LegacyField label="เลขเครื่อง" value={mortgage?.ENGNO} />
              <LegacyField label="ปีผลิต" value={mortgage?.MANUYR} />
              <LegacyField
                label="จดทะเบียน"
                value={formatDate(mortgage?.REGDATE)}
              />
              <LegacyField
                label="ทะเบียนหมด"
                value={formatDate(mortgage?.REGEND)}
              />
            </Row>
          </Col>

          {/* ขวา: หมายเหตุ/บันทึก */}
          <Col xs={24} lg={8}>
            <LegacySectionBar>หมายเหตุ / บันทึก</LegacySectionBar>
            <div
              style={{
                border: "1px solid #c3d0e0",
                borderRadius: 4,
                background: "#fffef0",
                padding: 10,
                minHeight: 320,
                whiteSpace: "pre-wrap",
                fontSize: 14,
              }}
            >
              {memoDataOld?.MEMO1 || "ไม่มีบันทึก"}
            </div>
          </Col>
        </Row>

        <Divider style={{ margin: "20px 0" }} />

        {/* ล่าง: ผู้เช่าซื้อ + ผู้ค้ำประกัน */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <LegacySectionBar>ผู้เช่าซื้อ</LegacySectionBar>
            <div
              style={{
                border: "1px solid #c3d0e0",
                borderRadius: 4,
                padding: 10,
                background: "#f4f8fc",
                height: "calc(100% - 30px)",
              }}
            >
              <LegacyPersonBlock
                title={`${customer?.SNAM || ""}${customer?.NAME1 || ""} ${customer?.NAME2 || ""}`.trim()}
                cuscod={customer?.CUSCOD}
                birthdt={customer?.BIRTHDT}
                addr={addr}
                occup={customer?.OCCUP}
                offic={customer?.OFFIC}
              />
            </div>
          </Col>
          {[0, 1].map((idx) => {
            const g = guarantors?.[idx];
            if (!g) return null;
            return (
              <Col xs={24} lg={8} key={idx}>
                <LegacySectionBar>
                  ผู้ค้ำประกันลำดับที่ {g?.GARNO ?? idx + 1}
                </LegacySectionBar>
                <div
                  style={{
                    border: "1px solid #c3d0e0",
                    borderRadius: 4,
                    padding: 10,
                    background: "#fbf5ee",
                    height: "calc(100% - 30px)",
                  }}
                >
                  <LegacyPersonBlock
                    title={`${g?.SNAM || ""}${g?.NAME1 || ""} ${g?.NAME2 || ""}`.trim()}
                    relatn={g?.RELATN}
                    cuscod={g?.CUSCOD}
                    birthdt={g?.BIRTHDT}
                    addr={g?.ADDRESS?.[0]}
                    occup={g?.OCCUP}
                    offic={g?.OFFIC}
                  />
                </div>
              </Col>
            );
          })}
        </Row>
      </StyledCard>
    );
  };

  const renderMortgageInfo = () => {
    const mortgage = loanData?.MORTGAGE;
    if (!mortgage) return;
    return (
      <StyledCard
        title="ข้อมูลหลักประกัน"
        icon={<CarOutlined style={{ color: THEME.primary, fontSize: 18 }} />}
      >
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <div
              style={{
                background: `linear-gradient(135deg, ${THEME.light} 0%, #e2e8f0 100%)`,
                padding: 20,
                borderRadius: 10,
                marginBottom: 16,
              }}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  ยี่ห้อ / รุ่น
                </Text>
                <Title level={4} style={{ margin: 0, color: THEME.primary }}>
                  {mortgage?.TYPE} {mortgage?.MODEL}
                </Title>
                <Text>{mortgage?.BAAB}</Text>
                <Space>
                  <Tag color="blue">ปี {mortgage?.MANUYR}</Tag>
                  <Tag color="geekblue">สี{mortgage?.COLOR}</Tag>
                </Space>
              </Space>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <Row gutter={[16, 12]}>
              <Col span={12}>
                <InfoItem
                  label="ทะเบียนรถ"
                  value={
                    <Text copyable style={{ fontFamily: "monospace" }}>
                      {mortgage?.REGNO}
                    </Text>
                  }
                />
              </Col>
              <Col span={12}>
                <InfoItem
                  label="จังหวัด"
                  value={
                    <Text copyable style={{ fontFamily: "monospace" }}>
                      {mortgage?.DORECV}
                    </Text>
                  }
                />
              </Col>
              <Col span={24}>
                <InfoItem
                  label="เลขตัวถัง"
                  value={
                    <Text copyable style={{ fontFamily: "monospace" }}>
                      {mortgage?.STRNO}
                    </Text>
                  }
                />
              </Col>
              <Col span={24}>
                <InfoItem
                  label="เลขเครื่อง"
                  value={
                    <Text copyable style={{ fontFamily: "monospace" }}>
                      {mortgage?.ENGNO}
                    </Text>
                  }
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </StyledCard>
    );
  };

  // ==================== TAB 5: ข้อมูลบุคคล ====================
  const renderCustomerInfo = () => {
    const customer = loanData?.CUSTOMER;
    const addresses = customer?.ADDRESS || [];
    if (!customer) return <Empty description="ไม่มีข้อมูล" />;
    return (
      <StyledCard
        title="ผู้เช่าซื้อ"
        icon={<UserOutlined style={{ color: THEME.primary, fontSize: 18 }} />}
        style={{ marginBottom: 16 }}
      >
        <Row gutter={[24, 16]}>
          <Col xs={24} lg={12}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                padding: "20px",
                background: THEME.light,
                borderRadius: 10,
                marginBottom: 16,
              }}
            >
              <Avatar
                size={72}
                style={{ background: THEME.gradient }}
                icon={<UserOutlined />}
              />
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  {customer?.SNAM}
                  {customer?.NAME1} {customer?.NAME2}
                </Title>
              </div>
            </div>
            <Row gutter={[16, 12]}>
              <Col span={24}>
                <Text type="secondary" style={{ fontSize: 16 }}>
                  <IdcardOutlined style={{ marginRight: 6 }} />
                  เลขบัตรประชาชน
                </Text>
                <br />
                <Text copyable strong>
                  {customer?.CUSCOD}
                </Text>
              </Col>
              <Col span={12}>
                <InfoItem
                  label="วันเกิด"
                  value={formatDate(customer?.BIRTHDT)}
                  icon={<CalendarOutlined />}
                />
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 16 }}>
                  <IdcardOutlined style={{ marginRight: 6 }} />
                  อาชีพ
                </Text>
                <br />
                <Text copyable strong>
                  {customer?.OCCUP || "-"}
                </Text>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 16 }}>
                  <IdcardOutlined style={{ marginRight: 6 }} />
                  ที่ทำงาน
                </Text>
                <br />
                <Text copyable strong style={{ fontSize: 16 }}>
                  {customer?.OFFIC || "-"}
                </Text>
              </Col>
            </Row>
          </Col>
          <Col xs={24} lg={12}>
            <div
              style={{
                padding: 20,
                background: `${THEME.info}08`,
                borderRadius: 10,
                borderLeft: `4px solid ${THEME.info}`,
                height: "100%",
              }}
            >
              {addresses.map((addr, idx) => (
                <dev key={addr.ADDRNO || idx}>
                  {idx > 0 && <Divider style={{ margin: "8px 0" }} />}
                  <Text strong style={{ color: THEME.info }}>
                    <HomeOutlined style={{ marginRight: 8 }} />
                    ที่อยู่{"   "}
                    {addr.ADDRNO}
                  </Text>
                  <br />
                  <Text copyable style={{ fontSize: 16 }}>
                    {`${addr.ADDR1} ${addr.ADDR2}  ตำบล. ${addr.TUMB} อำเภอ. ${addr.AUMPDES} จังหวัด. ${addr.PROVDES} รหัสไปษรณีย์. ${addr.ZIP}`.trim()}
                  </Text>
                  <br />
                  <Space>
                    <PhoneOutlined style={{ color: THEME.primary }} />
                    <Text strong copyable>
                      {addr.TELP || "-"}
                    </Text>
                  </Space>
                </dev>
              ))}
              {addresses.length === 0 && <Text>-</Text>}
            </div>
          </Col>
        </Row>
      </StyledCard>
    );
  };

  const renderGuarantors = () => {
    const guarantors = loanData?.GUARANTORS || [];
    if (guarantors?.length === 0) return;
    return (
      <StyledCard
        title={`ผู้ค้ำประกัน (${guarantors?.length} คน)`}
        icon={<TeamOutlined style={{ color: THEME.primary, fontSize: 18 }} />}
      >
        {guarantors?.length === 0 ? (
          <Empty description="ไม่มีข้อมูลผู้ค้ำประกัน" />
        ) : (
          <Row gutter={[16, 16]}>
            {guarantors?.map((g, idx) => {
              return (
                <Col xs={24} lg={12} key={idx}>
                  <div
                    style={{
                      padding: 20,
                      background: THEME.light,
                      borderRadius: 10,
                      borderLeft: `4px solid ${THEME.accent}`,
                      height: "100%",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 16,
                      }}
                    >
                      <Avatar size={56} style={{ background: THEME.accent }}>
                        {g.NAME1?.charAt(0)}
                      </Avatar>
                      <div>
                        <Text strong style={{ fontSize: 18 }}>
                          {g.SNAM}
                          {g.NAME1} {g.NAME2}
                        </Text>
                        <br />
                        <Space size={4}>
                          <Tag color="orange">คนค้ำที่ {g.GARNO}</Tag>
                          <Tag>{g.RELATN}</Tag>
                        </Space>
                      </div>
                    </div>
                    <Row gutter={[12, 8]}>
                      <Col span={24}>
                        <Text type="secondary" style={{ fontSize: 16 }}>
                          <IdcardOutlined style={{ marginRight: 6 }} />
                          เลขบัตรประชาชน
                        </Text>
                        <br />
                        <Text copyable strong>
                          {g.CUSCOD}
                        </Text>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary" style={{ fontSize: 16 }}>
                          วันเกิด
                        </Text>
                        <br />
                        <Text style={{ fontSize: 18 }}>
                          {formatDate(g.BIRTHDT)}
                        </Text>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary" style={{ fontSize: 16 }}>
                          อาชีพ
                        </Text>
                        <br />
                        <Text style={{ fontSize: 18 }}>{g?.OCCUP || "-"}</Text>
                      </Col>
                      <Col span={24}>
                        <Text type="secondary" style={{ fontSize: 16 }}>
                          ที่ทำงาน
                        </Text>
                        <br />
                        <Text style={{ fontSize: 16 }} copyable>
                          {g.OFFIC || "-"}
                        </Text>
                      </Col>
                    </Row>
                    <Divider style={{ margin: "12px 0" }} />
                    <div
                      style={{
                        padding: 12,
                        background: "#fff",
                        borderRadius: 8,
                      }}
                    >
                      {(g.ADDRESS || []).map((addr, addrIdx) => (
                        <React.Fragment key={addr.ADDRNO || addrIdx}>
                          {addrIdx > 0 && (
                            <Divider style={{ margin: "8px 0" }} />
                          )}
                          <Text type="secondary" style={{ fontSize: 16 }}>
                            <HomeOutlined style={{ marginRight: 6 }} />
                            ที่อยู่ {addr.ADDRNO}
                          </Text>
                          <br />
                          <Text style={{ fontSize: 16 }} copyable>
                            {`${addr.ADDR1} ${addr.ADDR2} ตำบล. ${addr.TUMB} อำเภอ. ${addr.AUMPDES} จังหวัด. ${addr.PROVDES} รหัสไปรษณีย์.${addr.ZIP}`.trim()}
                          </Text>
                          <br />
                          <Space style={{ marginTop: 8 }}>
                            <PhoneOutlined style={{ color: THEME.success }} />
                            <Text strong copyable>
                              {addr.TELP || "-"}
                            </Text>
                          </Space>
                        </React.Fragment>
                      ))}
                      {(!g.ADDRESS || g.ADDRESS.length === 0) && <Text>-</Text>}
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        )}
      </StyledCard>
    );
  };

  const renderMemo = () => {
    return (
      <>
        <Divider orientation="left">
          <Space>
            <Typography.Text
              strong
              onClick={() => setShowNoteOld(!showNoteOld)}
              style={{ cursor: "pointer" }}
            >
              ข้อมูลจาก "Smart-dev"
            </Typography.Text>
            <Tag
              color="blue"
              onClick={() => setShowNoteOld(!showNoteOld)}
              style={{ cursor: "pointer" }}
            >
              {!showNoteOld ? <DownSquareOutlined /> : <UpSquareOutlined />}
            </Tag>
          </Space>
        </Divider>

        {showNoteOld && memoDataOld && (
          <Card style={{ marginBottom: 8 }}>
            <Input.TextArea
              readOnly={editMemoOld}
              value={memoDataOld?.MEMO1}
              onChange={(e) =>
                setMemoDataOld({ ...memoDataOld, MEMO1: e.target.value })
              }
              autoSize
              style={{
                border: !editMemoOld ? "1.5px solid #ff4d4f" : undefined, // ✅ สีแดงเมื่อแก้ไข
                borderRadius: 6,
                transition: "border 0.3s",
                backgroundColor: !editMemoOld ? "#fff5f5" : "#fafafa", // ✅ พื้นหลังชมพูอ่อน
              }}
            />
            <Row>
              <Col span={24} style={{ textAlign: "center" }}>
                <Space style={{ marginTop: 10 }}>
                  {editMemoOld ? (
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => setEditMemoOld(false)}
                      disabled={![1, 2, 3, 13].includes(roleId)}
                    >
                      แก้ไข
                    </Button>
                  ) : (
                    <Button
                      danger
                      icon={<CloseOutlined />}
                      onClick={() => setEditMemoOld(true)}
                    >
                      ยกเลิก
                    </Button>
                  )}
                  <Popconfirm
                    placement="topLeft"
                    title="อัพเดทข้อมูลโปรแกรม Smart dev"
                    description="กรุณาตรวจสอบข้อมูลให้เรียบร้อย !"
                    onConfirm={postMemoOld} // ✅
                    okText="ยืนยัน"
                    cancelText="ปิด"
                    okButtonProps={{ size: "large" }}
                    cancelButtonProps={{ size: "large" }}
                  >
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      disabled={editMemoOld}
                    >
                      บันทึก
                    </Button>
                  </Popconfirm>
                </Space>
              </Col>
            </Row>
          </Card>
        )}

        <Divider orientation="left">
          <Space>
            <Typography.Text
              strong
              onClick={() => {
                setShowNoteNew(!showNoteNew);
              }}
              style={{ cursor: "pointer" }}
            >
              ข้อมูลจาก "โปรแกรมใหม่"
            </Typography.Text>
            <Tag
              color="blue"
              onClick={() => {
                setShowNoteNew(!showNoteNew);
              }}
              style={{ cursor: "pointer" }}
            >
              {memoDataNew ? memoDataNew?.length : 0} รายการ{" "}
              {!showNoteNew ? <DownSquareOutlined /> : <UpSquareOutlined />}
            </Tag>
          </Space>
        </Divider>
        {showNoteNew && (
          <>
            <List
              style={{ marginBottom: 16, borderRadius: 8, overflow: "hidden" }}
              bordered
              dataSource={memoDataNew}
              locale={{ emptyText: "ไม่มีข้อมูลหมายเหตุ" }}
              renderItem={(item, index) => (
                <List.Item
                  style={{
                    backgroundColor: index % 2 === 0 ? "#f0f7ff" : "#ffffff",
                    padding: "12px 16px",
                    alignItems: "flex-start",
                  }}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? "#1890ff" : "#096dd9",
                          fontWeight: "bold",
                        }}
                      >
                        {item?.USERNAME?.charAt(0)?.toUpperCase()}
                      </Avatar>
                    }
                    title={
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 4,
                        }}
                      >
                        <Space size={6}>
                          <Typography.Text strong style={{ color: "#1a365d" }}>
                            {item?.USERNAME}
                          </Typography.Text>
                          <Tag
                            color="blue"
                            style={{ borderRadius: 10, fontSize: 11 }}
                          >
                            {dayjs(item?.CreatedAt).format("DD/MM/YYYY HH:mm")}
                          </Tag>
                        </Space>
                        <Typography.Text
                          copyable={{
                            text: `${item?.note}\n ${dayjs(item?.CreatedAt).format("DD/MM/YYYY HH:mm")} [${item?.USERNAME}]`,
                            tooltips: ["คัดลอก", "คัดลอกแล้ว!"],
                          }}
                          style={{ fontSize: 12, color: "#888" }}
                        />
                      </div>
                    }
                    description={
                      <Typography.Text
                        style={{
                          fontSize: 13,
                          color: "#333",
                          whiteSpace: "pre-wrap",
                          lineHeight: 1.7,
                        }}
                      >
                        {item?.note}
                      </Typography.Text>
                    }
                  />
                </List.Item>
              )}
            />
            <TextArea
              placeholder="กรอกหมายเหตุ (ถ้ามี)"
              value={remarkValueNew}
              onChange={(e) => setRemarkValueNew(e.target.value)}
              rows={5}
              showCount
              maxLength={500}
              style={{ marginBottom: "20px" }}
            />
            <Row>
              <Col span={24} style={{ textAlign: "center" }}>
                <Popconfirm
                  placement="topLeft"
                  title="อัพเดทข้อมูลโปรแกรมใหม่"
                  description="กรุณาตรวจสอบข้อมูลให้เรียบร้อย ไม่สามารถแก้ไขสิ่งที่ได้บันทึกได้!"
                  onConfirm={postMemoNew} // ✅
                  okText="ยืนยัน"
                  cancelText="ปิด"
                  okButtonProps={{ size: "large" }}
                  cancelButtonProps={{ size: "large" }}
                >
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    disabled={!remarkValueNew}
                  >
                    บันทึก
                  </Button>
                </Popconfirm>
              </Col>
            </Row>
          </>
        )}
      </>
    );
  };

  const renderUploadImge = () => {
    // ── sort: menuType ASC → imageType ASC ───────────────────────
    const sorted = [...fileListByContractCourt].sort((a, b) =>
      a.menuType !== b.menuType
        ? a.menuType - b.menuType
        : a.imageType - b.imageType,
    );

    // ── แบ่ง Zone ตาม menuType ────────────────────────────────────
    const zone5 = sorted.filter((f) => f.menuType === 5);
    const zone6 = sorted.filter((f) => f.menuType === 6);
    const zone8 = sorted.filter((f) => f.menuType === 8);

    const ZONE_CONFIG = {
      5: {
        title: "เอกสารคำฟ้อง",
        color: "#d46b08",
        bg: "#fff7e6",
        border: "#ffd591",
      },
      6: {
        title: "เอกสารชั้นศาล",
        color: "#096dd9",
        bg: "#e6f4ff",
        border: "#91caff",
      },
      8: {
        title: "เอกสารปิดบัญชี",
        color: "#389e0d",
        bg: "#f6ffed",
        border: "#b7eb8f",
      },
    };

    // ── card รูปภาพ ───────────────────────────────────────────────
    const renderImageCard = (file) => {
      const previewUrl = `${GET_LOAD_IMAGE_NEW_BY}/${file.path}?token=${token}`;
      const { label, color } = getDocLabel(file.menuType, file.imageType);

      return (
        <Col key={file.imageId} xs={8} sm={6} md={4}>
          <div
            style={{
              border: `1px solid ${color}`,
              borderRadius: 8,
              padding: 8,
              textAlign: "center",
              background: "#fff",
            }}
          >
            <Image
              src={previewUrl}
              alt={file.path}
              style={{
                width: "100%",
                height: 80,
                objectFit: "cover",
                borderRadius: 4,
              }}
              preview={{
                mask: (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <EyeOutlined style={{ fontSize: 14 }} />
                    <span style={{ fontSize: 10 }}>ดูรูป</span>
                  </div>
                ),
              }}
            />
            <br />
            <div
              style={{
                marginTop: 6,
                background: color,
                color: "#fff",
                fontSize: 11,
                borderRadius: 4,
                padding: "1px 6px",
                display: "inline-block",
              }}
            >
              {label}
            </div>
          </div>
        </Col>
      );
    };

    // ── card PDF ──────────────────────────────────────────────────
    const renderPdfCard = (file) => {
      const previewUrl = `${GET_LOAD_IMAGE_NEW_BY}/${file.path}?token=${token}`;
      const { label, color } = getDocLabel(file.menuType, file.imageType);
      return (
        <Col key={file.imageId} xs={8} sm={6} md={4}>
          <div
            style={{
              border: `1px solid ${color}`,
              borderRadius: 8,
              padding: 8,
              textAlign: "center",
              background: "#fff",
            }}
          >
            <div
              onClick={() => {
                setPdfPreviewUrl(previewUrl);
                setPdfPreviewOpen(true);
              }}
              style={{
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: 80,
                background: "#fff1f0",
                borderRadius: 6,
                border: "1px dashed #ff4d4f",
                gap: 4,
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              <FilePdfOutlined style={{ fontSize: 32, color: "#ff4d4f" }} />
              <span style={{ fontSize: 11, color: "#ff4d4f", fontWeight: 500 }}>
                <EyeOutlined style={{ marginRight: 4 }} />
                ดู PDF
              </span>
            </div>
            <div
              style={{
                marginTop: 6,
                background: color,
                color: "#fff",
                fontSize: 11,
                borderRadius: 4,
                padding: "1px 6px",
                display: "inline-block",
              }}
            >
              {label}
            </div>
          </div>
        </Col>
      );
    };

    // ── render Zone ───────────────────────────────────────────────
    const renderZone = (files, menuType) => {
      if (!files.length) return null;
      const cfg = ZONE_CONFIG[menuType];
      const pdfFiles = files.filter((f) => getFileType(f.path) === "pdf");
      const imgFiles = files.filter((f) => getFileType(f.path) === "image");

      return (
        <div
          key={menuType}
          style={{
            border: `1px solid ${cfg.border}`,
            borderRadius: 10,
            padding: "12px 16px",
            marginBottom: 16,
            background: cfg.bg,
          }}
        >
          {/* Zone Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 12,
              paddingBottom: 8,
              borderBottom: `1px solid ${cfg.border}`,
            }}
          >
            <div
              style={{
                background: cfg.color,
                color: "#fff",
                borderRadius: 6,
                padding: "2px 12px",
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              {cfg.title}
            </div>
            <span style={{ fontSize: 14, color: "#888" }}>
              {files.length} ไฟล์
            </span>
          </div>

          {/* รูปภาพ */}
          {imgFiles.length > 0 && (
            <>
              <div style={{ fontSize: 14, color: "#888", marginBottom: 6 }}>
                รูปภาพ ({imgFiles.length})
              </div>
              <Image.PreviewGroup>
                <Row
                  gutter={[8, 8]}
                  style={{ marginBottom: pdfFiles.length ? 12 : 0 }}
                >
                  {imgFiles.map(renderImageCard)}
                </Row>
              </Image.PreviewGroup>
            </>
          )}

          {/* PDF */}
          {pdfFiles.length > 0 && (
            <>
              <div
                style={{
                  fontSize: 14,
                  color: "#888",
                  marginBottom: 6,
                  marginTop: imgFiles.length ? 4 : 0,
                }}
              >
                <FilePdfOutlined style={{ color: "#ff4d4f", marginRight: 4 }} />
                เอกสาร PDF ({pdfFiles.length})
              </div>
              <Row gutter={[8, 8]}>{pdfFiles.map(renderPdfCard)}</Row>
            </>
          )}
        </div>
      );
    };

    return (
      <>
        <Button
          variant="outlined"
          color="blue"
          onClick={() => {
            setIsModalAddImge(true);
          }}
        >
          อัพโหลด รูป/ไฟล์+
        </Button>
        <Divider orientation="center">
          <FileSearchOutlined /> ดูเอกสารทนาย
        </Divider>

        {renderZone(zone5, 5)}
        {renderZone(zone6, 6)}
        {renderZone(zone8, 8)}

        <PdfPreviewModal
          open={pdfPreviewOpen}
          url={pdfPreviewUrl}
          onClose={() => {
            setPdfPreviewOpen(false);
            setPdfPreviewUrl("");
          }}
        />
      </>
    );
  };

  const renderFollowCustomer = () => {
    return (
      <>
        <Divider orientation="left">
          <Space>
            <Typography.Text strong style={{ cursor: "pointer" }}>
              ข้อมูลจาก "โปรแกรมใหม่"
            </Typography.Text>
            <Tag color="blue" style={{ cursor: "pointer" }}>
              {followCusData ? followCusData?.length : 0} รายการ
            </Tag>
          </Space>
        </Divider>

        <Row gutter={[0, 16]}>
          <Col span={24}>
            <List
              style={{ borderRadius: 8, overflow: "hidden" }}
              bordered
              dataSource={followCusData}
              locale={{ emptyText: "ไม่มีข้อมูลติดตาม" }}
              renderItem={(item, index) => (
                <List.Item
                  style={{
                    backgroundColor: index % 2 === 0 ? "#f0f7ff" : "#ffffff",
                    padding: "12px 16px",
                    alignItems: "stretch", // เพื่อให้ความสูงของกล่องยืดเท่ากัน
                    width: "100%",
                  }}
                >
                  {/* Layout ซ้าย-ขวา */}
                  <Row
                    style={{ width: "100%", alignItems: "stretch" }} // เพิ่ม alignItems stretch ตรงนี้ด้วย
                    gutter={[16, 0]}
                  >
                    {/* ฝั่งซ้าย: ข้อมูล */}
                    <Col
                      xs={18}
                      sm={8}
                      md={6}
                      style={{
                        // ใช้ borderRight เป็นเส้นกั้นแนวตั้ง
                        borderRight: "1px solid #d9d9d9",
                        paddingRight: "16px", // เพิ่มระยะห่างก่อนถึงเส้น
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center", // จัดให้อยู่ตรงกลางแนวตั้ง
                      }}
                    >
                      <Space
                        direction="vertical"
                        size={4}
                        style={{ width: "100%" }}
                      >
                        {/* RUNNO + สถานะ */}
                        <Space size={4} wrap>
                          <Tag
                            color="geekblue"
                            style={{ borderRadius: 10, fontSize: 14 }}
                          >
                            {item?.RUNNO}
                          </Tag>
                          {/* <Tag
                            color={item?.CALLSTAT === "Y" ? "success" : "error"}
                            style={{ borderRadius: 10, fontSize: 11 }}
                          >
                            {item?.CALLSTAT === "Y" ? "โทรติด" : "โทรไม่ติด"}
                          </Tag> */}
                        </Space>

                        {/* รหัสพนักงาน */}
                        <Space size={4}>
                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: 14, minWidth: 100 }}
                          >
                            รหัสพนักงาน :
                          </Typography.Text>
                          <Typography.Text
                            strong
                            style={{ color: "#1a365d", fontSize: 14 }}
                          >
                            {item?.USERID}
                          </Typography.Text>
                        </Space>

                        {/* โทรเมื่อ */}
                        <Space size={4}>
                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: 14, minWidth: 100 }}
                          >
                            📞 โทรเมื่อ :
                          </Typography.Text>
                          <Tag
                            color="blue"
                            style={{
                              borderRadius: 10,
                              fontSize: 14,
                              margin: 0,
                            }}
                          >
                            {dayjs(item?.TELDT).format("DD/MM/YYYY")}
                          </Tag>
                        </Space>

                        {/* ลูกค้านัด */}
                        <Space size={4}>
                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: 14, minWidth: 100 }}
                          >
                            📅 ลูกค้านัด :
                          </Typography.Text>
                          {item?.MEETDT ? (
                            <Tag
                              color="purple"
                              style={{
                                borderRadius: 10,
                                fontSize: 14,
                                margin: 0,
                              }}
                            >
                              {dayjs(item?.MEETDT).format("DD/MM/YYYY")}
                            </Tag>
                          ) : (
                            <Typography.Text
                              type="secondary"
                              style={{ fontSize: 14 }}
                            >
                              -
                            </Typography.Text>
                          )}
                        </Space>

                        {/* ลงข้อมูลเมื่อ */}
                        <Space size={4}>
                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: 14, minWidth: 100 }}
                          >
                            🕐 ลงข้อมูลเมื่อ :
                          </Typography.Text>
                          <Typography.Text
                            style={{ fontSize: 14, color: "#555" }}
                          >
                            {dayjs(item?.INPDATE).format("DD/MM/YYYY HH:mm")}
                          </Typography.Text>
                        </Space>
                      </Space>
                    </Col>

                    {/* ฝั่งขวา: REMARK */}
                    <Col
                      xs={24}
                      sm={12}
                      md={14}
                      style={{
                        paddingLeft: "10px",
                      }}
                    >
                      <Tag color="blue"> หมายเหตุ : </Tag>

                      <Typography.Text
                        style={{
                          fontSize: 16,
                          color: "#333",
                          whiteSpace: "pre-wrap",
                          lineHeight: 1.8,
                          flex: 1,
                        }}
                        copyable={{
                          text: `${item?.REMARK}\nโทร: ${dayjs(item?.TELDT).format("DD/MM/YYYY")}${item?.MEETDT ? ` | นัด: ${dayjs(item?.MEETDT).format("DD/MM/YYYY")}` : ""}\nบันทึกโดย: ${item?.USERID}`,
                          tooltips: ["คัดลอก", "คัดลอกแล้ว!"],
                        }}
                      >
                        {item?.REMARK || (
                          <Typography.Text type="secondary">-</Typography.Text>
                        )}
                      </Typography.Text>
                    </Col>
                  </Row>
                </List.Item>
              )}
            />
          </Col>
        </Row>
      </>
    );
  };

  const renderMailCustomer = () => {
    const judge = judgeData?.judge?.[0];
    const mail = mailData || {};

    // ── helpers ──
    const fmtDate = (d) => (d ? dayjs(d).format("DD MMM YYYY") : "-");

    const replyTag = (statusVal) => {
      const opt = mailStatusOptions.find(
        (o) => Number(o.value) === Number(statusVal),
      );
      if (!opt)
        return <Tag style={{ borderRadius: 10, margin: 0 }}>ยังไม่ตอบกลับ</Tag>;
      return (
        <Tag color={opt.color} style={{ borderRadius: 10, margin: 0 }}>
          {opt.icon}
          {opt.label}
        </Tag>
      );
    };

    const emsText = (codeVal) =>
      statusEms?.find((s) => Number(s.code) === Number(codeVal))?.status ?? "-";

    const sortByNewest = (list = []) =>
      [...list].sort(
        (a, b) =>
          dayjs(b.createdDate || b.datetime).valueOf() -
          dayjs(a.createdDate || a.datetime).valueOf(),
      );

    const field = (label, node) => (
      <Col key={label} xs={24} sm={12} md={8}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {label}
        </Typography.Text>
        <div
          style={{
            fontSize: 14,
            color: "#1a365d",
            fontWeight: 500,
            marginTop: 2,
          }}
        >
          {node}
        </div>
      </Col>
    );

    const renderMailItem = (item, idx, accent) => (
      <div
        key={item.id}
        style={{
          border: "1px solid #eef0f3",
          borderLeft: `4px solid ${accent}`,
          borderRadius: 10,
          padding: "12px 16px",
          background: "#fff",
          marginBottom: 10,
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 10,
          }}
        >
          <Space size={8}>
            <span
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                background: accent,
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
              }}
            >
              {idx + 1}
            </span>
            <Typography.Text strong style={{ fontSize: 15 }}>
              {item.customerFullname || "-"}
            </Typography.Text>
            <Tag color={item.customerTypeId === 1 ? "geekblue" : "blue"}>
              {item.customerTypeId === 1 ? "ผู้ค้ำ" : "ผู้เช่าซื้อ"}
            </Tag>
          </Space>
          {replyTag(item.statusReply)}
        </div>

        <Row gutter={[12, 10]}>
          {field(
            "เลขพัสดุ (ส่ง)",
            <Tag color="blue" style={{ margin: 0 }}>
              {item.parcelNo || "-"}
            </Tag>,
          )}
          {field(
            "เลขพัสดุ (ตอบกลับ)",
            <Tag color="cyan" style={{ margin: 0 }}>
              {item.parcelNoResponse || "-"}
            </Tag>,
          )}
          {field("วันที่ส่ง", fmtDate(item.datetime || item.dateParcelNo))}
          {field("วันที่ตอบกลับ", fmtDate(item.dateReply))}
          {field("สถานะ (EMS)", emsText(item.statusPostWeb))}
          {field("วันนำจ่าย", fmtDate(item.datePostWeb))}
          {field(
            "ยืนยันเจอจดหมายตอบกลับ",
            item.flagConfirm ? `✓ ${fmtDate(item.dateConfirm)}` : "-",
          )}
        </Row>
      </div>
    );

    // เนื้อหา 1 section (เรียงใหม่→เก่า)
    const sectionBody = (list = [], accent) => {
      const sorted = sortByNewest(list);
      return sorted.length > 0 ? (
        sorted.map((item, idx) => renderMailItem(item, idx, accent))
      ) : (
        <Empty
          description="ไม่มีข้อมูล"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ margin: "8px 0" }}
        />
      );
    };

    // หัวข้อ section (จุดสี + ชื่อ + จำนวน)
    const sectionLabel = (title, count, accent) => (
      <Space size={8}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: accent,
            display: "inline-block",
          }}
        />
        <Typography.Text strong style={{ fontSize: 15 }}>
          {title}
        </Typography.Text>
        <Tag color={accent} style={{ borderRadius: 10, margin: 0 }}>
          {count} รายการ
        </Tag>
      </Space>
    );

    // ── ทำยอม (judge) — การ์ดเดี่ยว ──
    const renderTamYom = () => {
      if (!judge) {
        return (
          <Empty
            description="ไม่มีข้อมูลทำยอม"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        );
      }
      const accent = "#1677ff";
      return (
        <div
          style={{
            border: "1px solid #eef0f3",
            borderLeft: `4px solid ${accent}`,
            borderRadius: 10,
            padding: "12px 16px",
            background: "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <Row gutter={[12, 10]}>
            {field(
              "เลขพัสดุ",
              <Tag color="blue" style={{ margin: 0 }}>
                {judge.parcelNo || "-"}
              </Tag>,
            )}
            {field("วันที่บันทึก", fmtDate(judge.dateParcelNo))}
            {field("สถานะ (EMS)", emsText(judge.statusPostWeb))}
            {field(
              "วันที่นำส่ง",
              judge.datePostWeb === "0001-01-01T00:00:00Z" || !judge.datePostWeb
                ? "-"
                : fmtDate(judge.datePostWeb),
            )}
          </Row>
        </div>
      );
    };

    const demand = mail.workDemandGuaranter || [];
    const cancel = mail.workCancelContract || [];
    const repurchase = mail.letterCarRepurchase || [];

    // รายการ Collapse แต่ละ section
    const items = [
      {
        key: "tamyom",
        label: sectionLabel(
          "ส่งเอกสาร ทำยอม",
          judgeData?.flagLoanNew === 1 ? 1 : 0,
          "#1677ff",
        ),
        children: renderTamYom(),
      },
      {
        key: "demand",
        label: sectionLabel("ส่งเอกสาร บอกกล่าว", demand.length, "#722ed1"),
        children: sectionBody(demand, "#722ed1"),
      },
      {
        key: "cancel",
        label: sectionLabel("ส่งเอกสาร บอกเลิก", cancel.length, "#fa8c16"),
        children: sectionBody(cancel, "#fa8c16"),
      },
      {
        key: "repurchase",
        label: sectionLabel(
          "ส่งเอกสาร ใช้สิทธิ์ซื้อรถคืน",
          repurchase.length,
          "#13c2c2",
        ),
        children: sectionBody(repurchase, "#13c2c2"),
      },
    ];

    // เปิดอัตโนมัติเฉพาะ section ที่มีข้อมูล (+ ทำยอมเสมอ)
    const defaultOpen = [
      "tamyom",
      demand.length ? "demand" : null,
      cancel.length ? "cancel" : null,
      repurchase.length ? "repurchase" : null,
    ].filter(Boolean);

    return (
      <Collapse
        items={items}
        defaultActiveKey={defaultOpen}
        expandIconPosition="end"
        bordered={false}
        style={{ background: "transparent" }}
      />
    );
  };

  // ==================== TAB ITEMS ====================
  const tabItems = [
    {
      key: "1",
      label: (
        <span>
          <AuditOutlined /> ข้อมูลหลังพิพากษา
        </span>
      ),
      children: (
        <Spin spinning={loadingJudge}>
          <div>
            {judgeData ? (
              <>{renderJudgementInfo()}</>
            ) : (
              !judgeData && <Empty description="ไม่มีข้อมูล" />
            )}
          </div>
        </Spin>
      ),
    },
    {
      key: "2",
      label: (
        <span>
          <FileTextOutlined /> ข้อมูลสัญญา(ใหม่)
        </span>
      ),
      children: (
        <Spin spinning={loadingJudge}>
          <>
            <div>
              {judgeData?.loanNew
                ? renderLoanNewInfo()
                : renderLoanNewInfoJudge()}
              {renderMortgageInfo()}
            </div>
          </>
        </Spin>
      ),
    },
    {
      key: "11",
      label: (
        <span>
          <AuditOutlined /> ค่าใช้จ่าย
        </span>
      ),
      children: (
        <Spin spinning={loadingJudge}>
          <div>{<>{renderPaymentInCase()}</>}</div>
        </Spin>
      ),
    },

    {
      key: "3",
      label: (
        <span>
          <TransactionOutlined /> ตารางรับชำระ
        </span>
      ),
      children: (
        <Spin spinning={loadingJudge}>
          <>
            <div>{renderPayment()}</div>
          </>
        </Spin>
      ),
    },
    (judgeData?.loanNew || judgeData?.loanJudge) && {
      key: "4",
      label: (
        <span>
          <FileTextOutlined /> ตารางดิว
        </span>
      ),
      children: (
        <Spin spinning={loadingJudge}>
          <>
            <div>{renderduePayment()}</div>
          </>
        </Spin>
      ),
    },
    {
      key: "5",
      label: (
        <span>
          <FileTextOutlined /> ข้อมูลทรัพย์
        </span>
      ),
      children: (
        <Spin spinning={loadingJudge}>
          <div>
            {judgeData?.asset ? (
              <>{renderInvestigationInfo()}</>
            ) : (
              !judgeData?.asset && <Empty description="ไม่มีข้อมูล" />
            )}
          </div>
        </Spin>
      ),
    },
    {
      key: "6",
      label: (
        <span>
          <FileTextOutlined /> ข้อมูลสัญญา(เก่า)
        </span>
      ),
      children: (
        <Spin spinning={loadingLoan} tip="กำลังโหลดข้อมูลสัญญา...">
          <div>
            {loanData ? (
              <>{renderLegacyContractView()}</>
            ) : (
              !loadingLoan && <Empty description="ไม่มีข้อมูล" />
            )}
          </div>
        </Spin>
      ),
    },
    {
      key: "7",
      label: (
        <span>
          <SolutionOutlined /> ข้อมูลบุคคล
        </span>
      ),
      children: (
        <Spin spinning={loadingLoan} tip="กำลังโหลดข้อมูลบุคคล...">
          <div>
            {loanData ? (
              <>
                {renderCustomerInfo()}
                {renderGuarantors()}
              </>
            ) : (
              !loadingLoan && <Empty description="ไม่มีข้อมูล" />
            )}
          </div>
        </Spin>
      ),
    },

    {
      key: "9",
      label: (
        <span>
          <FolderOpenOutlined /> ไฟล์เอกสารในสัญญา
        </span>
      ),
      children: (
        <Spin spinning={loadingImge} tip="กำลังโหลดข้อมู...">
          <div>
            <>{renderUploadImge()}</>
          </div>
        </Spin>
      ),
    },
    {
      key: "8",
      label: (
        <span>
          <TwitchOutlined /> memo
        </span>
      ),
      children: (
        <Spin spinning={loadingLoan} tip="กำลังโหลดข้อมูลบุคคล...">
          <div>
            {memoDataOld ? (
              <>{renderMemo()}</>
            ) : (
              !memoDataOld && <Empty description="ไม่มีข้อมูล" />
            )}
          </div>
        </Spin>
      ),
    },
    {
      key: "10",
      label: (
        <span>
          <EyeOutlined /> บันทึกติดตาม
        </span>
      ),
      children: (
        <Spin spinning={loadingImge} tip="กำลังโหลดข้อมู...">
          <div>
            <>{renderFollowCustomer()}</>
          </div>
        </Spin>
      ),
    },
    {
      key: "13",
      label: (
        <span>
          <MailOutlined /> การส่งเอกสารหาลูกค้า
        </span>
      ),
      children: (
        <Spin spinning={loadingJudge} tip="กำลังโหลดข้อมู...">
          <div>
            <>{renderMailCustomer()}</>
          </div>
        </Spin>
      ),
    },
  ].filter(Boolean);

  // ── ค้นหาเลขสัญญาในหน้านี้เลย (ไม่พึ่งช่องค้นหาบน Header) ──
  const handleLocalSearch = () => {
    if (!searchInput.trim()) {
      message.warning("กรุณากรอกเลขสัญญา");
      return;
    }
    handleSearch({ CONTNO: searchInput.trim() });
  };

  const renderContractSearchBar = () => (
    <div
      style={{
        marginBottom: 16,
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <Space.Compact>
        <Input
          placeholder="ค้นหาเลขที่สัญญา"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={handleLocalSearch}
          allowClear
          style={{ width: 240 }}
        />
        <Button
          type="primary"
          icon={<FileSearchOutlined />}
          onClick={handleLocalSearch}
        >
          ค้นหา
        </Button>
      </Space.Compact>
    </div>
  );

  // ==================== MAIN RENDER ====================
  if (!judgeData && !loadingJudge) {
    return (
      <>
        {renderContractSearchBar()}
        <Card
          style={{
            borderRadius: THEME.borderRadius,
            boxShadow: THEME.cardShadow,
            textAlign: "center",
            padding: 40,
          }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical">
                <Text strong style={{ fontSize: 16 }}>
                  ไม่มีข้อมูล
                </Text>
                <Text type="secondary">
                  กรุณาค้นหาเลขสัญญาเพื่อดูรายละเอียด
                </Text>
              </Space>
            }
          />
        </Card>
      </>
    );
  }

  const handleBillRequest = async () => {
    let newContno;
    let code_contno;

    if (judgeData?.judge?.length > 0) {
      newContno = judgeData.judge[0].newContno;
      code_contno = newContno?.replace(/^[A-Za-z]/, "86").replace(/-/g, "");
    } else {
      newContno = searchData.CONTNO;
    }

    console.log("judgeData?.judge[0]?.CONTNO", judgeData);

    let freshLoanData;

    if (!loanData) {
      freshLoanData = await loadLoanData(newContno);
    } else {
      freshLoanData = loanData;
    }

    if (
      !judgeData ||
      judgeData?.details === "ไม่พบข้อมูล" ||
      !judgeData?.loanNew
    ) {
      console.log("newContno", newContno);
      console.log("1.ไม่พบข้อมูล");

      const initialData = {
        company: {
          label: "บริษัท วัน มันนี่ จำกัด (สำนักงานใหญ่)",
          value: "one_money",
          address:
            "89 หมู่ 14 ถ.มิตรภาพ ต.ในเมือง อ.เมืองขอนแก่น จ.ขอนแก่น 40000",
          tel: "043-239-888",
          license: "T0406-000",
        },
        pay: [
          {
            CONTNO:
              judgeData?.judge?.length > 0
                ? judgeData.judge[0].CONTNO
                : freshLoanData?.LOAN?.CONTNO, // ใช้ freshLoanData แทน loanData (state)
            newContno: newContno,
            judgement: "0",
            judgementLack: "0",
            judgementInterest: "0",
            interestRate: "0",
            lackOfBenefits: "0",
            interestRateOfLack: "0",
            trackingFee: "0",
            attorneyFees: "0",
            fee: "0",
            flagLoanNew: 1,
            TOT_UPAY: freshLoanData?.LOAN?.TOT_UPAY?.toString(),
            T_NOPAY: freshLoanData?.LOAN?.T_NOPAY?.toString(),
            NCSHPRC: freshLoanData?.LOAN?.NCSHPRC?.toString(),
            CODE_CONTNO:
              judgeData?.judge?.length > 0
                ? code_contno
                : freshLoanData?.LOAN?.CONTNO,
            CUSTOMER_ID: null,
            CUSCOD: freshLoanData?.CUSTOMER?.CUSCOD,
            ref: judgeData?.judge?.length > 0 ? "001" : "006",
            SNAM: freshLoanData?.CUSTOMER?.SNAM,
            NAME1: freshLoanData?.CUSTOMER?.NAME1,
            NAME2: freshLoanData?.CUSTOMER?.NAME2,
            addressTr: freshLoanData?.CUSTOMER?.ADDRESS || null,
          },
        ],
      };

      console.log("initialData", initialData);
      setBillPayment(initialData);
      setLoanData(freshLoanData); // sync state ด้วย
    } else {
      console.log("2.พบข้อมูล");
      loadBillPayment();
    }

    setIsModalPayIn(true);
  };

  const showRender = () => {
    return (
      <>
        {renderContractHeader()}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 12,
          }}
        >
          {judgeData ? (
            <Space size={10}>
              {judgeData.flagLoanNew === 2 && !judgeData.loanJudge && (
                <Button
                  color="purple"
                  variant="outlined"
                  icon={<FileProtectOutlined />}
                  onClick={async () => {
                    console.log("judgeData", judgeData);
                    const paylod = {
                      idJudge: judgeData.judge?.[0]?.idJudge,
                    };
                    console.log("paylod", paylod);
                    const result = await postApporve(paylod);
                    setLoadApprovDue(false);
                    console.log("สร้างตารางดิวของ idJudge นี้ไปแล้ว", result);
                    message.success(result.message);
                    loadJudgeData(searchData);
                  }}
                >
                  อนุมัติขอผ่อนชำระ
                </Button>
              )}
              {judgeData.judge && (
                <Button
                  color="green"
                  variant="outlined"
                  icon={<FileProtectOutlined />}
                  onClick={() => {
                    setIsModalBillAfterCourtOldData(true);
                  }}
                >
                  ดูใบส่งสินค้า
                </Button>
              )}
              <Button
                ghost
                variant="outlined"
                color="red"
                icon={<FileSearchOutlined />}
                onClick={() => {
                  if (!loanData) {
                    loadLoanData(judgeData?.judge[0]?.CONTNO);
                    setIsModalPreviewPayment(true);
                  } else {
                    setIsModalPreviewPayment(true);
                  }
                }}
                disabled={!judgeData?.ACTIVE && !judgeData?.judge}
                size="large"
                style={{ minWidth: 120, borderRadius: 8 }}
                // disabled={true}
              >
                {judgeData?.flagLoanNew === 1 || judgeData?.loanJudge !== null
                  ? "พรีวิว เบี้ยปรับ"
                  : "พรีวิว ยอดจ่าย"}
              </Button>
              <Button
                type="primary"
                ghost
                icon={<FileTextOutlined />}
                onClick={() => handleBillRequest()}
                disabled={!judgeData?.ACTIVE && !judgeData?.judge}
                size="large"
                style={{ minWidth: 120, borderRadius: 8 }}
              >
                ใบเสร็จรับชำระ
              </Button>{" "}
            </Space>
          ) : null}
        </div>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          type="card"
          size="large"
          items={tabItems}
          style={{
            background: "#fff",
            padding: "16px",
            borderRadius: THEME.borderRadius,
            boxShadow: THEME.cardShadow,
          }}
        />
      </>
    );
  };

  const handleReload = () => {
    setFileListByContractCourt([]);
    loadImge(true);
  };

  const currentStatus = judgeData?.judge?.[0]?.currentStatus;
  const hasData = judgeData?.details !== "ไม่พบข้อมูล";

  const canShowRender =
    (hasData &&
      userId === 35 &&
      HIA_APPROVED_STATUSES.includes(currentStatus)) ||
    (judgeData && userId !== 35 && roleId === 12 && currentStatus >= 16) ||
    roleId !== 12;

  return (
    <Spin spinning={loadingJudge} size="large" tip="กำลังโหลดข้อมูล...">
      <div style={{ padding: "0 0 24px" }}>
        {renderContractSearchBar()}
        {canShowRender ? (
          showRender()
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical">
                <Text strong style={{ fontSize: 16 }}>
                  ไม่มีข้อมูล
                </Text>
                <Text type="secondary">
                  กรุณาค้นหาเลขสัญญาเพื่อดูรายละเอียด
                </Text>
              </Space>
            }
          />
        )}
      </div>
      {isModalpayIn && (
        <PayInSlipModal
          open={isModalpayIn}
          onClose={() => setIsModalPayIn(false)}
          dataDB2={loanData}
          billPayment={billPayment}
        />
      )}
      {isModalAddImge && (
        <AddImage
          open={isModalAddImge}
          close={() => setIsModalAddImge(false)}
          dataDefault={judgeData}
          onSuccess={handleReload}
        />
      )}
      {isModalPreviewPayment && (
        <PreviewPayment
          open={isModalPreviewPayment}
          close={() => setIsModalPreviewPayment(false)}
          dataDefault={judgeData}
          dataContno={loanData}
        />
      )}

      {isModalBillAfterCourtOldData && (
        <BillAfterCourtOldData
          open={isModalBillAfterCourtOldData}
          close={() => setIsModalBillAfterCourtOldData(false)}
          dataDefault={judgeData?.judge?.[0]}
          status={judgeData?.flagLoanNew}
        />
      )}
    </Spin>
  );
};

const ContractDetailMeeting = MotionHoc(Main);
export default ContractDetailMeeting;
